/**
 * Nederlandse berichten. Zelfde sleutelboom als en.ts (de standaardtaal);
 * alleen de waarden verschillen. Bij een nieuwe key: eerst in en.ts, dan
 * hier de vertaling toevoegen.
 */
export default {
  common: {
    login: 'Inloggen',
    tryFree: 'Gratis proberen',
    languageLabel: 'Taal',
    product: 'Product',
    pricing: 'Prijzen',
    faq: 'Veelgestelde vragen',
    contact: 'Contact',
    save: 'Opslaan',
    cancel: 'Annuleren',
    loading: 'Laden…',
    yes: 'Ja',
    no: 'Nee',
  },

  toast: {
    regionLabel: 'Meldingen',
    dismiss: 'Sluit melding: {title}',
  },

  modal: {
    close: 'Sluiten',
  },

  rte: {
    toolbar: 'Opmaak',
    content: 'Tekstinhoud',
    heading: 'Kop {level}',
    bold: 'Vet',
    italic: 'Cursief',
    underline: 'Onderstrepen',
    bulletList: 'Opsommingslijst',
    orderedList: 'Genummerde lijst',
    link: 'Link toevoegen of bewerken',
    linkShort: 'Link',
    insertTable: 'Tabel invoegen',
    insertImage: 'Afbeelding invoegen',
    imageSettings: 'Afbeeldingsinstellingen',
    imageAlt: 'Alt-tekst',
    imageAltHint: 'Beschrijft de afbeelding voor wie hem niet ziet. Laat leeg als hij puur decoratief is.',
    tableToolbar: 'Tabelbewerking',
    deleteTable: 'Tabel verwijderen',
    linkPrompt: 'Link-URL (laat leeg om de link te verwijderen):',
    altPrompt: 'Alt-tekst voor de afbeelding (beschrijving voor schermlezers):',
    uploadFailed: 'Afbeelding uploaden mislukt',
    textColor: 'Tekstkleur',
    textColorToolbar: 'Tekstkleur',
    fontSize: 'Tekstgrootte (px)',
    fontSizeDefault: 'auto',
    fontSizeReset: 'Terug naar de standaard tekstgrootte',
    fontSizeHeadingHint: 'Kopformaten komen uit het thema en zijn hier niet aanpasbaar.',
    textColorCustom: 'Aangepaste kleur',
    textColorDefault: 'Standaardkleur',
    align: {
      left: 'Links uitlijnen',
      center: 'Centreren',
      right: 'Rechts uitlijnen',
    },
    colors: {
      dark: 'Donker',
      gray: 'Grijs',
      red: 'Rood',
      orange: 'Oranje',
      green: 'Groen',
      blue: 'Blauw',
      purple: 'Paars',
      pink: 'Roze',
    },
    table: {
      rowBefore: '+ Rij boven',
      rowAfter: '+ Rij onder',
      rowDelete: '− Rij',
      colBefore: '+ Kolom links',
      colAfter: '+ Kolom rechts',
      colDelete: '− Kolom',
      headerRow: 'Kopregel',
      headerColumn: 'Kopkolom',
    },
  },

  breadcrumb: {
    label: 'Breadcrumb',
    home: 'Home',
  },

  home: {
    subtitle: 'Documenteer, beheer en deel je design system met je team',
    features: {
      pages: {
        title: "Pagina's",
        description: "Creëer en beheer documentatiepagina's met een flexibele page builder.",
      },
      figma: {
        title: 'Figma-integratie',
        description: 'Importeer designs direct vanuit Figma en synchroniseer automatisch.',
      },
      tokens: {
        title: 'Design tokens',
        description: 'Beheer kleuren, typografie en spacing als herbruikbare tokens.',
      },
      code: {
        title: 'Code-generatie',
        description: 'Genereer automatisch CSS, React, Vue en meer uit je designs.',
      },
    },
    a11yHeading: 'Toegankelijkheid',
    a11yBody:
      'Dit platform voldoet aan de WCAG 2.2 AA richtlijnen voor digitale toegankelijkheid. Gebruik Tab om te navigeren en Enter om te activeren.',
    onboarding: {
      title: 'Aan de slag',
      progress: '{done} van {total} stappen gedaan',
      dismiss: 'Checklist verbergen',
      optional: '(optioneel)',
      doneLabel: 'Gedaan',
      allDone: 'Alle stappen klaar - je documentatie staat.',
      steps: {
        figma: {
          title: 'Verbind met Figma',
          description: 'Log één keer in bij Figma, dan kan Pentrical je designbestanden lezen. Documenteren zonder Figma kan ook.',
        },
        import: {
          title: 'Importeer je eerste Figma-bestand',
          description: 'Plak de link van een Figma-bestand; maten, kleuren en tokens komen vanzelf mee.',
        },
        page: {
          title: 'Maak een categorie en je eerste pagina',
          description: 'Categorieën zijn de hoofdindeling van je documentatie; pagina\'s maak je in het menu links.',
        },
        publish: {
          title: 'Publiceer en bekijk als bezoeker',
          description: 'Een pagina is pas zichtbaar voor bezoekers na het publiceren. Met "Bekijk als bezoeker" zie je precies wat zij zien.',
        },
        team: {
          title: 'Nodig je team uit',
          description: 'Collega\'s die meeschrijven nodig je uit via Mijn account. Wie alleen leest heeft geen account nodig.',
        },
      },
    },
  },

  start: {
    title: 'Aan de slag met Pentrical',
    welcome:
      'Fijn dat je er bent. In vijf stappen staat je design-system-documentatie online - dit scherm houdt bij hoe ver je bent.',
    structure: {
      title: 'Zo zit Pentrical in elkaar',
      menu: 'Het menu bovenin zijn je categorieën - de hoofdstukken van je documentatie.',
      sidebar: 'De zijbalk links toont de pagina\'s binnen de categorie waar je in werkt.',
      publish: 'Een pagina wordt pas zichtbaar voor bezoekers zodra je haar publiceert.',
    },
    help: {
      title: 'Hulp nodig?',
      body: 'In het helpcenter staat korte uitleg over elk onderdeel van Pentrical.',
      link: 'Open het helpcenter',
    },
  },

  pageTabs: {
    nav: 'Paginatabs',
    renameLabel: 'Nieuwe tabnaam',
    draft: 'Concept',
    actionsFor: 'Acties voor tab {title}',
    newTabPlaceholder: 'Naam nieuwe tab…',
    newTabLabel: 'Naam nieuwe tab',
    addTab: 'Tab toevoegen',
    rename: 'Hernoemen',
    hideFromVisitors: 'Verbergen voor bezoekers',
    makeVisible: 'Zichtbaar maken',
    moveLeft: 'Naar links',
    moveRight: 'Naar rechts',
    delete: 'Verwijderen',
    noCategoryError: 'Deze pagina heeft geen categorie; tabs zijn niet mogelijk.',
    added: 'Tab "{title}" toegevoegd',
    addFailed: 'Tab toevoegen mislukt',
    renameFailed: 'Hernoemen mislukt',
    visibilityFailed: 'Zichtbaarheid wijzigen mislukt',
    moved: 'Tab "{title}" verplaatst naar positie {pos} van {total}',
    moveFailed: 'Verplaatsen mislukt',
    confirmDelete: 'Weet je zeker dat je tab "{title}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
    deleted: 'Tab "{title}" verwijderd',
    deleteFailed: 'Verwijderen mislukt',
  },


  pageEditor: {
    breadcrumbHome: 'Home',
    breadcrumbPages: 'Pagina\'s',
    breadcrumbEditor: 'Editor',

    loading: 'Pagina wordt geladen…',
    notFound: 'Pagina niet gevonden',
    backToPages: 'Terug naar pagina\'s',

    cancelBack: 'Annuleren en terug naar de pagina',
    published: 'Gepubliceerd',
    draft: 'Concept',
    notSaved: '• Niet opgeslagen',
    preview: 'Voorbeeld',
    previewPrompt: {
      title: 'Het voorbeeld toont de opgeslagen versie',
      body: 'Je hebt wijzigingen die nog niet zijn opgeslagen. Die zitten niet in het voorbeeld - sla eerst op, dan zie je de pagina precies zoals hij nu is.',
      publishedNote: 'Deze pagina staat live, dus opslaan publiceert deze wijzigingen meteen.',
      saveAndPreview: 'Opslaan en bekijken',
      previewAnyway: 'Bekijken zonder opslaan',
      cancel: 'Annuleren',
    },
    backToDraft: 'Naar concept',
    save: 'Opslaan',
    update: 'Bijwerken',
    publish: 'Publiceren',

    title: 'Titel',
    titlePlaceholder: 'Paginatitel',
    summary: 'Samenvatting',
    summaryHint: '(optioneel, staat onder de titel)',
    summaryPlaceholder: 'Korte omschrijving van deze pagina',
    tabSummaryNotice: 'De titel hierboven is de tabnaam. Bezoekers zien op deze tab de titel en samenvatting van de hoofdpagina —',
    tabSummaryEditHere: 'die bewerk je hier',
    tabSummaryEditParent: 'die bewerk je op de hoofdpagina',

    tabs: 'Tabs',
    tabsHint: '(subpagina\'s, zichtbaar als tabbalk en in het menu)',

    setAsHomepage: 'Als homepage instellen',
    setAsHomepageHint: 'Bezoekers zien deze pagina als eerste op de startpagina.',
    hideSidebar: 'Linkermenu verbergen',
    hideSidebarHint: 'De startpagina toont dan geen navigatiebalk links.',
    hideFromMenu: 'Niet tonen in het linkermenu',
    hideFromMenuHint: 'De pagina blijft de startpagina, maar verschijnt niet als item in het menu voor bezoekers.',

    showsFirstTab: 'Deze pagina toont de eerste tab',
    showsFirstTabHint: 'Een pagina met tabs heeft zelf geen content: bezoekers zien automatisch de eerste tab. Open een tab hierboven om de inhoud ervan te bewerken.',

    chooseLayout: 'Kies een layout:',
    cancel: 'Annuleren',
    addSectionTop: 'Sectie toevoegen bovenaan',
    addSectionAfter: 'Sectie toevoegen na sectie {n}',

    dragToMove: 'Sleep om de sectie te verplaatsen',
    sectionN: 'Sectie {n}',
    sectionLayout: 'Layout voor sectie {n}',
    sectionKind: 'Soort sectie {n}',
    sectionKinds: {
      standard: 'Standaard (rijen)',
      hero: 'Hero',
    },
    moveSectionUp: 'Verplaats sectie {n} omhoog',
    moveSectionDown: 'Verplaats sectie {n} omlaag',
    duplicate: 'Dupliceren',
    copy: 'Kopiëren',
    duplicateSectionN: 'Sectie {n} dupliceren',
    copySectionN: 'Sectie {n} kopiëren',
    pasteSectionTop: 'Sectie plakken',
    pasteSectionHere: 'Sectie plakken',
    deleteSectionN: 'Verwijder sectie {n}',

    sectionTitle: 'Sectietitel',
    optional: 'Optioneel',
    sectionTitleHint: 'Wordt als kop boven de sectie getoond op de pagina. Leeg laten toont geen titel.',
    backgroundColor: 'Achtergrondkleur',
    sectionBgColor: 'Achtergrondkleur voor sectie {n}',
    none: 'Geen',
    sectionBgColorHex: 'Achtergrondkleur hexwaarde voor sectie {n}',
    sectionBgColorClear: 'Achtergrondkleur van sectie {n} verwijderen',
    noBackgroundColor: 'Geen achtergrondkleur',

    sectionSettings: 'Sectie-instellingen',
    sectionSettingsN: 'Instellingen voor sectie {n}',
    toggleSectionN: 'Sectie {n} in- of uitklappen',
    collapseAll: 'Alles inklappen',
    expandAll: 'Alles uitklappen',
    sectionSettingsHint: 'Deze instellingen gelden alleen voor deze sectie en overrulen het sitethema. Laat een kleur leeg om het thema te volgen.',
    sectionTitleColor: 'Kleur sectietitel',
    sectionTitleColorClear: 'Titelkleur van sectie {n} verwijderen',
    sectionBorder: 'Rand',
    sectionBorderColor: 'Randkleur',
    sectionBorderColorClear: 'Randkleur van sectie {n} verwijderen',
    sectionWidth: 'Breedte',
    sectionWidthStandard: 'Standaardbreedte',
    sectionWidthFull: 'Volle breedte',
    sectionWidthHint: 'Volle breedte laat de sectie over het hele scherm lopen, net als een hero.',
    sectionCenterContent: 'Inhoud centreren',
    sectionCenterContentHint: 'De achtergrond loopt door over het hele scherm, terwijl de inhoud even breed blijft als de andere secties.',
    sectionPadding: 'Binnenruimte',
    sectionPaddingBreakpoint: 'Schermbreedte voor de binnenruimte',
    sectionPaddingHint: 'Ruimte tussen de rand van de sectie en de inhoud. Leeg is de standaard (24 px).',
    sectionPaddingMobileHint: 'Geldt onder 640 pixels. Leeg gebruikt de desktopwaarde.',
    desktop: 'Desktop',
    mobile: 'Mobiel',
    padding: {
      top: 'Boven',
      right: 'Rechts',
      bottom: 'Onder',
      left: 'Links',
    },

    rowVisibility: {
      label: 'Op welke schermen rij {n} verschijnt',
      all: 'Overal tonen',
      desktop: 'Alleen desktop',
      mobile: 'Alleen mobiel',
      hidden: 'Verborgen',
      hiddenBadge: 'Verborgen',
      hiddenRowLabel: 'Rij {n}, verborgen voor bezoekers',
    },
    rowSettings: 'Rij-instellingen',
    rowSettingsN: 'Instellingen voor rij {n}',
    rowSettingsHint: 'Deze instellingen gelden alleen voor deze rij. Laat een kleur leeg om het thema te volgen.',
    sectionBgImage: {
      label: 'Achtergrondafbeelding',
      upload: 'Klik om een achtergrondafbeelding te uploaden',
      replace: 'Vervangen',
      remove: 'Verwijderen',
      fit: 'Weergave',
      contrastHint: 'Controleer of de tekst er goed leesbaar overheen blijft.',
      fits: {
        cover: 'Vult de sectie',
        contain: 'Past erbinnen',
        original: 'Werkelijke grootte',
        repeat: 'Herhalen als tegels',
      },
      aligns: { top: 'Boven', center: 'Midden', bottom: 'Onder' },
    },
    rowBackground: {
      colorClear: 'Geen achtergrondkleur voor rij {n}',
    },
    rowPadding: {
      label: 'Ruimte binnen',
      labelN: 'Ruimte binnen rij {n}',
    },
    rowBorder: {
      label: 'Rand',
      labelN: 'Rand om rij {n}',
      color: 'Randkleur',
      colorClear: 'Terug naar de themakleur voor rij {n}',
    },
    imageLayout: {
      size: 'Breedte',
      align: 'Horizontaal',
      valign: 'Verticaal',
      alignDisabled: 'Op volle breedte valt er niets uit te lijnen.',
      hint: 'De verticale plek zie je zodra de kolom ernaast langer is.',
      sizes: {
        small: 'Klein (1/3)',
        medium: 'Middel (1/2)',
        large: 'Groot (3/4)',
        full: 'Volle breedte',
      },
      aligns: { left: 'Links', center: 'Gecentreerd', right: 'Rechts' },
      valigns: { top: 'Boven', middle: 'Midden', bottom: 'Onder' },
      corners: 'Hoeken',
      radius: 'Afronding (px)',
      cornerChoices: {
        theme: 'Volg het thema',
        rounded: 'Afgerond',
        square: 'Vierkant',
      },
    },

    heroBackground: 'Achtergrond',
    heroColor: 'Kleur',
    heroImage: 'Afbeelding',
    heroBgColor: 'Achtergrondkleur',
    heroBgColorHex: 'Achtergrondkleur hexwaarde',
    heroChangeImage: 'Andere afbeelding kiezen',
    heroUploadImage: 'Klik om afbeelding te uploaden',
    heroSizeHint: 'Het mooist bij 1600 x 600 pixels of groter. JPG, PNG of WebP, maximaal 5 MB.',
    heroFocus: {
      heading: 'Brandpunt',
      hint: 'De afbeelding wordt bijgesneden om de band te vullen. Klik of sleep op de afbeelding om te bepalen welk deel in beeld blijft; met de pijltjestoetsen kan het ook.',
      pickerAria: 'Brandpunt van de hero-afbeelding. Klik op de afbeelding of gebruik de pijltjestoetsen.',
      previewLabel: 'Zo wordt hij bijgesneden',
      readout: 'Brandpunt: {x}% van links, {y}% van boven',
      reset: 'Terug naar het midden',
    },
    heroAltPlaceholder: 'Alt-tekst (toegankelijkheid)',
    heroAltAria: 'Alt-tekst voor achtergrondafbeelding',
    heroHeading: 'Kop',
    heroTitlePlaceholder: 'Titel van de hero',
    heroSubtitle: 'Ondertekst',
    heroSubtitlePlaceholder: 'Korte ondertekst onder de kop',
    heroTextColor: 'Tekstkleur',
    heroLight: 'Licht',
    heroDark: 'Donker',

    row: 'Rij {n}',
    rowLayout: 'Indeling voor rij {n}',
    lastRowHint: 'Een sectie heeft minstens één rij nodig — verwijder anders de sectie.',
    dragToMoveRow: 'Sleep om de rij te verplaatsen',
    moveRowUp: 'Rij {n} omhoog verplaatsen',
    moveRowDown: 'Rij {n} omlaag verplaatsen',
    duplicateRowN: 'Rij {n} dupliceren',
    copyRowN: 'Rij {n} kopiëren',
    deleteRowN: 'Rij {n} verwijderen',
    addRow: 'Rij toevoegen',
    pasteRow: 'Rij plakken',
    addRowAtStart: 'Rij toevoegen vóór rij 1',
    addRowAtN: 'Rij toevoegen na rij {n}',
    pasteRowAtStart: 'Gekopieerde rij plakken vóór rij 1',
    pasteRowAtN: 'Gekopieerde rij plakken na rij {n}',
    chooseRowLayout: 'Kies een indeling voor de nieuwe rij:',

    column: 'Kolom {n}',
    columnOf: 'Kolom {n} van {total}',
    columnBgColor: 'Achtergrondkleur voor kolom {n}',
    columnBgColorHex: 'Achtergrondkleur hexwaarde voor kolom {n}',
    columnBgColorClear: 'Achtergrondkleur van kolom {n} verwijderen',
    columnSettings: 'Kolominstellingen',
    columnSettingsN: 'Instellingen voor kolom {n}',
    columnSettingsHint: 'Geldt alleen voor deze kolom. Leeg laten = de standaardopmaak volgen.',
    columnBorder: 'Rand',
    columnBorderN: 'Rand om kolom {n}',
    columnBorderReset: 'Automatisch',
    columnBorderResetN: 'Terug naar automatische rand voor kolom {n}',
    columnBorderAutoHint: 'Automatisch: tekst-, kaart- en afbeeldingkolommen krijgen geen kader, andere contenttypes wel.',
    columnBorderOwnFrameHint: 'Een call-out of divider tekent zijn eigen kader, dus deze kolom heeft geen rand en geen padding.',
    columnBorderColor: 'Randkleur',
    columnBorderColorClear: 'Randkleur van kolom {n} verwijderen',
    dragToMoveColumn: 'Sleep om de kolom te verplaatsen',
    moveColumnLeft: 'Kolom {n} naar links verplaatsen',
    moveColumnRight: 'Kolom {n} naar rechts verplaatsen',
    deleteColumnN: 'Kolom {n} verwijderen',
    lastColumnHint: 'Een rij heeft minstens één kolom nodig — verwijder anders de rij.',
    contentTypeForColumn: 'Content type voor kolom {n}',

    textContentForColumn: 'Tekst inhoud voor kolom {n}',
    textPlaceholder: 'Voer tekst in (Markdown ondersteund)…',
    markdownHint: 'Markdown wordt ondersteund: **vet**, *cursief*, # kopjes, - lijsten',

    removeImage: 'Afbeelding verwijderen',
    uploading: 'Uploaden…',
    cardUploadImageOptional: 'Klik om afbeelding te uploaden (optioneel)',
    altText: 'Alt tekst (toegankelijkheid)',
    describeImage: 'Beschrijf de afbeelding…',
    cardText: 'Tekst (optioneel)',
    cardLink: 'Link (optioneel) — maakt de hele card klikbaar',
    linkPlaceholder: '/pages/12 of https://voorbeeld.nl',

    uploadedImageAlt: 'Geüploade afbeelding',
    uploadImage: 'Klik om afbeelding te uploaden',
    caption: 'Onderschrift (optioneel)',
    captionPlaceholder: 'Bijschrift onder de afbeelding…',
    imageLink: 'Link (optioneel) — maakt de afbeelding klikbaar',

    codeForColumn: 'Code voor kolom {n}',
    codePlaceholder: '// Voer code in…',

    colorName: 'Kleur naam',
    hexValue: 'Hex waarde',
    unnamed: 'Naamloos',

    previewLoading: 'Preview wordt opgehaald…',
    removeFigmaDesign: 'Figma-design verwijderen',
    displaySize: 'Weergaveformaat',
    chooseOtherDesign: 'Ander design kiezen',
    chooseFigmaDesign: 'Figma-design kiezen',
    browseLibrary: 'Blader door de bibliotheek en kies een scherm',

    noSections: 'Nog geen secties',
    noSectionsHint: 'Voeg je eerste sectie toe om te beginnen met bouwen.',
    addFirstSection: 'Eerste sectie toevoegen',

    sectionTypes: {
      '1-col': '1 Kolom',
      '2-col': '2 Kolommen',
      '3-col': '3 Kolommen',
      '4-col': '4 Kolommen',
      hero: 'Hero',
    },

    callout: {
      icon: 'Icoon',
      uploadIcon: 'Eigen icoon uploaden (PNG/WebP)',
      removeIcon: 'Icoon verwijderen',
      layout: 'Indeling',
      layoutInline: 'Icoon links, tekst rechts',
      layoutStacked: 'Icoon erboven, tekst eronder',
      iconAlign: 'Uitlijning icoon',
      iconVerticalAlign: 'Uitlijning icoon (verticaal)',
      alignLeft: 'Links',
      alignCenter: 'Gecentreerd',
      alignRight: 'Rechts',
      alignTop: 'Boven',
      alignMiddle: 'Midden',
      alignBottom: 'Onder',
      iconSize: 'Icoongrootte (px)',
      iconColor: 'Icoonkleur',
      text: 'Tekst',
    },

    divider: {
      heightLabel: 'Hoogte (px)',
      showLine: 'Lijn tonen',
      showLineHint: 'Zet uit om de divider puur als spacer te gebruiken, zonder zichtbare lijn.',
      lineColor: 'Lijnkleur',
      preview: 'Voorbeeld',
    },

    componentStatus: {
      label: 'Status van het component',
      hint: '(optioneel)',
      none: 'Geen status',
    },

    mcpConnect: {
      explainer:
        'Toont bezoekers het adres waarmee ze hun eigen AI-tool aan deze styleguide koppelen. Het adres wordt automatisch bepaald; je hoeft niets in te vullen.',
      titleLabel: 'Eigen kop (optioneel)',
      introLabel: 'Eigen tekst (optioneel)',
      showSteps: 'Stappen tonen',
    },

    storybook: {
      explainer:
        'Toont één story uit je gepubliceerde Storybook, zodat het draaiende component naast de afspraken erover staat.',
      storyLabel: 'Story',
      storyPlaceholder: 'Plak het adres van een story, of het story-id',
      storyHint:
        'Open de story in je Storybook en kopieer het adres uit de adresbalk. Het story-id wordt er automatisch uit gehaald.',
      storyResolved: 'Story-id: {id}',
      storyInvalid: 'Hier zit geen bruikbare story in. Plak het adres van een story uit je Storybook.',
      heightLabel: 'Hoogte in pixels',
      notConnected: 'Er is nog geen Storybook gekoppeld.',
      notConnectedLink: 'Adres instellen',
      pickerLabel: 'Component',
      pickerPlaceholder: 'Kies een component uit je Storybook',
      pickerLoading: 'Componenten ophalen uit je Storybook...',
      pickerUnavailable:
        'De lijst met componenten kon niet uit je Storybook worden opgehaald. Plak hieronder het adres van een story.',
      pickerNotStorybook:
        'Op {url} staat geen Storybook. Staat jullie Storybook in een submap, neem die dan mee in het adres, bijvoorbeeld {url}/storybook. Aanpassen kan bij',
      pickerRefresh: 'Lijst vernieuwen',
      docsOption: 'Documentatie',
      orPasteLabel: 'Of plak het adres van een story',
      embedBlocked:
        'Je Storybook weigert om in een andere site getoond te worden, dus dit blok blijft voor je lezers leeg. Wat daaraan te doen is staat bij',
      embedBlockedLink: 'Instellingen, Storybook',
    },

    blockTypes: {
      text: 'Tekst (Markdown)',
      richtext: 'Tekst (editor)',
      figma_design: 'Figma Design',
      text_styles: 'Tekststijlen',
      card: 'Card',
      callout: 'Call-out',
      divider: 'Divider',
      image: 'Afbeelding',
      code: 'Code',
      color_swatch: 'Kleur Swatch',
      mcp_connect: 'AI-koppeling',
      storybook: 'Storybook',
    },

    textStyles: {
      choose: 'Tekststijlen kiezen',
      browse: 'Kies een Figma-bestand en vink de stijlen aan',
      chooseOther: 'Andere stijlen kiezen',
      remove: 'Tekststijlen verwijderen',
      chosenCount: '{count} stijl(en) uit {file}',
      unavailable: 'Deze stijlen zijn niet meer beschikbaar. Kies opnieuw.',
      moveUp: 'Stijl omhoog',
      moveDown: 'Stijl omlaag',
      removeStyle: 'Stijl verwijderen uit dit blok',
      options: 'Weergave',
      sampleText: 'Voorbeeldtekst',
      sampleTextPlaceholder: 'Laat leeg voor de standaardzin',
      remBase: 'rem-basis',
      remBaseHint: 'Waar de rem-waarden op gebaseerd zijn (standaard 16px)',
      specPosition: 'Specificaties',
      specPositionLeft: 'Links',
      specPositionRight: 'Rechts',
      specPositionAbove: 'Boven',
      visibleSpecs: 'Zichtbare regels',
    },

    colorSwatch: {
      source: 'Bron',
      sourceManual: 'Handmatig',
      sourceFigma: 'Uit Figma',
      chooseStyle: 'Stijl kiezen',
      changeStyle: 'Andere stijl kiezen',
      detach: 'Loskoppelen',
      detachHint: 'Maakt hiervan een losse, handmatige swatch — een latere wijziging in Figma werkt dan niet meer door.',
      unavailable: 'Deze kleurstijl is niet meer beschikbaar in Figma.',
    },

    sizes: {
      small: 'Klein',
      medium: 'Middel',
      large: 'Groot',
    },

    lock: {
      bannerTitle: '{name} bewerkt deze pagina nu',
      bannerBody: 'Je kunt meekijken, maar niet bewerken totdat die klaar is. Dit scherm ontgrendelt vanzelf.',
      takeover: 'Bewerken overnemen',
      confirmTakeover: 'Bewerken overnemen van {name}? Diens niet-opgeslagen wijzigingen kunnen verloren gaan.',
      available: 'Deze pagina is weer vrij — je kunt nu bewerken.',
      someone: 'Iemand anders',
      saveBlocked: 'Opslaan mislukt: {name} heeft het bewerken van deze pagina overgenomen.',
    },

    revisions: {
      editedBy: 'Bewerkt {time} door {name}',
      edited: 'Bewerkt {time}',
      title: 'Versiegeschiedenis',
      shortLabel: 'Historie',
      intro: 'Elke keer opslaan maakt een versie. Open er een om te bekijken, en zet hem desgewenst terug.',
      close: 'Versiegeschiedenis sluiten',
      empty: 'Nog geen eerdere versies. Zodra je deze pagina opslaat, verschijnen ze hier.',
      current: 'Huidige versie',
      causes: {
        draft: 'Concept opgeslagen',
        published: 'Gepubliceerd',
        restore: 'Versie teruggezet',
      },
      by: 'door {name}',
      view: 'Bekijk de versie van {time}',
      previewBannerTitle: 'Je bekijkt de versie van {time}',
      previewBannerBody: 'Dit is alleen kijken — er is nog niets veranderd. Zet deze versie terug om er de huidige van te maken, of ga terug.',
      restore: 'Deze versie terugzetten',
      restoring: 'Terugzetten…',
      exitPreview: 'Terug naar de huidige versie',
      confirmRestore: 'De huidige inhoud vervangen door deze versie? De huidige versie blijft in de geschiedenis staan, dus je kunt altijd terug.',
      restored: 'Eerdere versie teruggezet.',
      restoreFailed: 'Deze versie kon niet teruggezet worden',
      loadFailed: 'Deze versie kon niet geladen worden',
      timeJustNow: 'zojuist',
      timeMinutes: '{n} minuut geleden | {n} minuten geleden',
      timeHours: '{n} uur geleden | {n} uur geleden',
      timeDays: '{n} dag geleden | {n} dagen geleden',
    },

    messages: {
      confirmUnsaved: 'Je hebt niet-opgeslagen wijzigingen. Weet je zeker dat je wilt vertrekken?',
      unsavedWarning: 'Je hebt niet-opgeslagen wijzigingen.',
      confirmDeleteSection: 'Weet je zeker dat je deze sectie wilt verwijderen?',
      confirmDeleteRow: 'Weet je zeker dat je deze rij en de inhoud ervan wilt verwijderen?',
      confirmDeleteColumn: 'Weet je zeker dat je deze kolom en de inhoud ervan wilt verwijderen?',
      columnMovedTo: 'Kolom verplaatst naar positie {pos} van {total}.',
      columnMoved: 'Kolom verplaatst.',
      columnDeleted: 'Kolom verwijderd.',
      rowAdded: 'Rij toegevoegd.',
      rowDeleted: 'Rij verwijderd.',
      sectionMovedTo: 'Sectie verplaatst naar positie {pos} van {total}.',
      sectionMoved: 'Sectie verplaatst.',
      sectionDuplicated: 'Sectie gedupliceerd.',
      sectionCopied: 'Sectie gekopieerd.',
      copyFailed: 'Kopiëren mislukt: deze inhoud kan niet gekopieerd worden.',
      copiedSessionOnly: 'Gekopieerd — alleen in dit tabblad beschikbaar (opslag is vol).',
      sectionPasted: 'Sectie geplakt.',
      rowMovedTo: 'Rij verplaatst naar positie {pos} van {total}.',
      rowMoved: 'Rij verplaatst.',
      rowDuplicated: 'Rij gedupliceerd.',
      rowCopied: 'Rij gekopieerd.',
      rowPasted: 'Rij geplakt.',
      homepageSet: 'Ingesteld als homepage',
      homepageUnset: 'Homepage-aanwijzing verwijderd',
      homepageChangeFailed: 'Kon homepage niet wijzigen',
      sidebarHidden: 'Linkermenu verborgen op de homepage',
      sidebarVisible: 'Linkermenu weer zichtbaar',
      settingChangeFailed: 'Kon instelling niet wijzigen',
      hiddenFromMenu: 'Verborgen uit het linkermenu',
      visibleInMenu: 'Weer zichtbaar in het linkermenu',
      visibilityChangeFailed: 'Kon zichtbaarheid niet wijzigen',
      imageUploaded: 'Afbeelding geüpload!',
      uploadFailed: 'Upload mislukt',
      changesUpdated: 'Wijzigingen bijgewerkt',
      pagePublished: 'Pagina gepubliceerd!',
      revertedToDraft: 'Teruggezet naar concept',
      savedAsDraft: 'Opgeslagen als concept',
      saveFailed: 'Opslaan mislukt',
    },
  },

  notFound: {
    title: 'Pagina niet gevonden',
    body: 'De pagina die je zoekt bestaat niet of is verplaatst.',
    home: 'Naar home',
    back: 'Terug',
    cantFind: 'Kun je niet vinden wat je zoekt?',
    viewAll: "Bekijk alle pagina's",
  },

  unknownEnvironment: {
    title: 'Deze omgeving bestaat niet',
    body: 'Er hoort geen documentatie bij dit webadres. Controleer of het adres klopt — een typefout in de naam is de meest voorkomende oorzaak.',
    hint: 'Weet je zeker dat het adres goed is? Vraag het dan na bij degene die je de link stuurde.',
  },

  a11yStatement: {
    title: 'Toegankelijkheidsverklaring',
    intro: 'Wij streven ernaar Pentrical toegankelijk te maken voor iedereen, inclusief mensen met een beperking.',
    complianceHeading: 'Nalevingsstatus',
    compliancePre: 'Dit platform voldoet aan de',
    wcagLink: 'Web Content Accessibility Guidelines (WCAG) 2.2 niveau AA',
    measuresHeading: 'Toegankelijkheidsmaatregelen',
    measuresIntro: 'Wij nemen de volgende maatregelen om de toegankelijkheid te waarborgen:',
    measures: [
      'Toegankelijkheid is onderdeel van onze ontwikkelingsprocessen',
      'Alle nieuwe functies worden getest op toegankelijkheid',
      'Wij gebruiken semantische HTML',
      'Alle interactieve elementen zijn bereikbaar via het toetsenbord',
      'Kleurcontrasten voldoen aan WCAG AA-normen (minimaal 4.5:1 voor tekst)',
      'Klikbare en aanraakbare elementen zijn minimaal 24x24px (WCAG 2.5.8)',
      'Formulieren hebben duidelijke labels en foutmeldingen',
      'Afbeeldingen hebben alternatieve teksten',
      'De interface werkt met schermleessoftware',
    ],
    techHeading: 'Ondersteunende technologieën',
    techIntro: 'Dit platform is getest met de volgende technologieën:',
    tech: [
      'NVDA (schermlezer) met Firefox',
      'VoiceOver (schermlezer) met Safari',
      'Toetsenbordnavigatie in alle moderne browsers',
      'Browser zoom tot 200%',
    ],
    limitationsHeading: 'Bekende beperkingen',
    limitationsIntro: 'Ondanks onze inspanningen kunnen sommige onderdelen nog niet volledig toegankelijk zijn:',
    limitationFigmaLabel: 'Figma embeds:',
    limitationFigmaBody:
      "Embedded Figma designs zijn afhankelijk van Figma's toegankelijkheidsniveau. We bieden alternatieve tekst en specificaties.",
    limitationCodeLabel: 'Code syntax highlighting:',
    limitationCodeBody:
      'Sommige kleurcombinaties in code blokken kunnen onvoldoende contrast hebben. De code is echter nog steeds leesbaar en kopieerbaar.',
    contactHeading: 'Feedback en contact',
    contactIntro:
      'Wij verwelkomen uw feedback over de toegankelijkheid van dit platform. Als u problemen ondervindt of suggesties heeft:',
    contactEmailLabel: 'E-mail:',
    contactPhoneLabel: 'Telefoon:',
    contactResponse: 'Wij proberen binnen 5 werkdagen te reageren op uw feedback.',
    enforcementHeading: 'Handhavingsprocedure',
    enforcementBody:
      'Als u niet tevreden bent met onze reactie op uw feedback, kunt u contact opnemen met de relevante toezichthouder voor digitale toegankelijkheid in uw land.',
    techSpecsHeading: 'Technische specificaties',
    techSpecsIntro: 'De toegankelijkheid van dit platform is afhankelijk van de volgende technologieën:',
    techSpecsOutro:
      'Deze technologieën worden gebruikt in combinatie met de toegankelijkheidsfuncties van browsers en schermleessoftware.',
    evalHeading: 'Evaluatiemethode',
    evalIntro: 'Dit platform is geëvalueerd met behulp van:',
    eval: [
      'Geautomatiseerde tests (axe-core, Lighthouse)',
      'Handmatige tests door experts',
      'Tests met schermleessoftware',
      'Tests met alleen toetsenbordnavigatie',
    ],
    updateHeading: 'Laatste update',
    updatePre: 'Deze verklaring is voor het laatst bijgewerkt op',
    updateDate: '1 januari 2024',
  },

  routeTitles: {
    insights: 'Analytics',
    hubs: 'Hubs',
    home: 'Home',
    landing: 'Pentrical — design system-documentatie',
    terms: 'Algemene voorwaarden',
    privacy: 'Privacyverklaring',
    disclaimer: 'Disclaimer',
    login: 'Inloggen',
    forgotPassword: 'Wachtwoord vergeten',
    resetPassword: 'Wachtwoord herstellen',
    handoff: 'Je wordt ingelogd',
    checkout: 'Betalen',
    signup: 'Account aanmaken',
    invitation: 'Uitnodiging accepteren',
    gettingStarted: 'Aan de slag',
    pages: "Pagina's",
    page: 'Pagina',
    docs: 'Documentatie',
    pageEditor: 'Pagina bewerken',
    figmaLibrary: 'Figma-bibliotheek',
    tokens: 'Design tokens',
    theme: 'Thema',
    access: 'Toegang',
    profile: 'Profiel',
    team: 'Team',
    communication: 'Communicatie',
    accessibility: 'Toegankelijkheid',
    billing: 'Abonnement & facturering',
    mcpServer: 'Pentrical MCP Server',
    storybook: 'Storybook',
    about: 'Over',
    figmaFile: 'Figma-bestand',
    figmaViewer: 'Interactieve viewer',
    accessibilityStatement: 'Toegankelijkheidsverklaring',
    notFound: 'Pagina niet gevonden',
  },

  a11y: {
    routeAnnounce: 'Navigatie naar {title} voltooid',
    routeAnnounceFallback: 'nieuwe pagina',
    skipToContent: 'Direct naar inhoud',
    menu: 'Menu',
    backToTop: 'Terug naar boven',
    mainNav: 'Hoofdnavigatie',
    mainNavMobile: 'Hoofdnavigatie (mobiel)',
    // Meldingen die alleen een schermlezer voorleest (WCAG 4.1.3).
    announce: {
      defaultItem: 'Inhoud',
      loading: '{item} wordt geladen…',
      loaded: '{item} is geladen',
      success: 'Succes: {action}',
      error: 'Fout: {error}',
      listNone: 'Geen {items} gevonden',
      listCount: '{count} {items} gevonden',
      navigation: 'Navigatie naar {destination}',
      dialogOpen: 'Dialoog geopend: {title}',
      dialogClose: 'Dialoog gesloten',
      formSubmitting: 'Formulier wordt verzonden…',
      formSubmitted: 'Formulier succesvol verzonden',
      copied: '{item} gekopieerd naar klembord',
    },
    notificationTypes: {
      success: 'Succes',
      error: 'Fout',
      warning: 'Waarschuwing',
      info: 'Informatie',
    },
  },

  landing: {
    hero: {
      eyebrow: 'Voor design- en developmentteams',
      titleLine1: 'Je design system,',
      titleAccent: 'altijd actueel.',
      titleLine3: 'Zonder handwerk.',
      sub: 'Pentrical koppelt Figma aan levende documentatie. Ontwikkelaars klikken op een design en zien direct maten, kleuren en tokens — richtlijnen en schermen op één plek die vanzelf bijblijft.',
      ctaPrimary: 'Start 14 dagen gratis',
      ctaSecondary: 'Bekijk hoe het werkt',
      chipsLabel: 'Zonder drempels',
      chipNoCard: 'Geen creditcard nodig',
      chipReady: 'Klaar in 2 minuten',
      chipEu: 'EU-hosting',
      mockUrl: 'jouwteam.pentrical.com',
      mockSave: 'Opslaan',
      mockEmail: 'E-mailadres',
    },

    specs: {
      buttonName: 'Button / Primary',
      swatchName: 'Token / primary-300',
      inputName: 'Input / Tekst',
      fill: 'Vulling',
      text: 'Tekst',
      radius: 'Radius',
      padding: 'Padding',
      contrast: 'Contrast',
      value: 'Waarde',
      usage: 'Gebruik',
      usageValue: 'accent, illustratie',
      textOn: 'Tekst erop',
      alsoAs: 'Ook als',
      alsoAsValue: 'CSS var + Figma-stijl',
      height: 'Hoogte',
      border: 'Rand',
      focus: 'Focus',
    },

    pains: {
      title: 'Herkenbaar?',
      sub: 'Design systems verouderen niet in Figma — ze verouderen in de documentatie eromheen.',
      retype: {
        title: 'Specs overtypen uit Figma',
        text: 'Ontwikkelaars vragen om maten en kleuren, designers zoeken ze op. Elke keer opnieuw.',
      },
      stale: {
        title: 'Documentatie die meteen veroudert',
        text: 'De wiki klopte bij het schrijven. Drie sprints later vertrouwt niemand hem meer.',
      },
      truth: {
        title: 'Iedereen een andere waarheid',
        text: 'Design in Figma, richtlijnen in Notion, tokens in een spreadsheet. Wat is de bron?',
      },
    },

    demo: {
      title: 'Klik op een element. Lees de specs.',
      sub: 'Dit is de kern van Pentrical — probeer het hieronder zelf.',
      canvasLabel: 'Voorbeelddesign — kies een element om de specificaties te zien',
      save: 'Opslaan',
      swatchSr: 'Kleurtoken primary-300',
      email: 'E-mailadres',
    },

    steps: {
      connect: {
        title: 'Koppel je Figma-account',
        text: 'Log in met Figma en kies de bestanden die je wilt documenteren. Geen plugins, geen export.',
      },
      sync: {
        title: 'Pentrical synchroniseert',
        text: 'Schermen, componenten, posities en design tokens worden automatisch opgehaald en actueel gehouden.',
      },
      read: {
        title: 'Je team klikt en leest',
        text: 'Ontwikkelaars klikken op een element en zien direct de specs. Richtlijnen en designs staan naast elkaar.',
      },
    },

    features: {
      eyebrow: 'Alles-in-één platform',
      titlePre: 'Alles wat je',
      titleAccent: 'design system',
      titlePost: 'nodig heeft',
      clickable: {
        title: 'Klikbare designs met specs',
        text: 'Klik op een element in het design en zie direct maten, kleuren, typografie en spacing — zonder Figma te openen.',
      },
      current: {
        title: 'Altijd actueel',
        text: 'Automatische synchronisatie met Figma houdt previews en specs bij zonder handwerk.',
      },
      tokens: {
        title: 'Design tokens',
        text: 'Kleuren, typografie en spacing als herbruikbare tokens, rechtstreeks uit je designbestanden.',
      },
      together: {
        title: 'Documentatie en designs samen',
        text: "Schrijf richtlijnen en sluit Figma-schermen direct in je pagina's in. Eén bron voor het hele design system.",
      },
      space: {
        title: 'Eigen omgeving voor je team',
        text: 'Je eigen subdomein met rollen en rechten: het team beheert, de hele organisatie leest mee.',
      },
      hosting: {
        title: 'Europese hosting',
        text: 'Je gegevens staan op servers binnen de EU en betalingen lopen via Paddle als merchant of record.',
      },
    },

    // Hier stond een 'testimonials'-blok met drie verzonnen klantquotes van
    // "Naam Achternaam" bij "Bedrijf". De sectie stond al uit, maar de teksten
    // gingen nog mee in de JS-bundle die bezoekers downloaden. Verwijderd;
    // pas terugzetten met echte, schriftelijk goedgekeurde quotes.

    reasons: {
      title: 'Waarom Pentrical?',
      sub: "Er zijn genoeg wiki's. Pentrical is gebouwd voor precies één ding: design systems die kloppen.",
      built: {
        title: 'Gebouwd voor design systems',
        text: 'Geen generieke wiki met plaatjes, maar een tool die componenten, tokens en specs echt begrijpt.',
      },
      source: {
        title: 'Figma als bron, niet als bijlage',
        text: 'De koppeling is het hart van het product. Je documentatie volgt je designs — niet andersom.',
      },
      european: {
        title: 'Europees, van hosting tot facturatie',
        text: 'EU-servers, AVG-proof, fonts zonder Google, betalingen en btw netjes via Paddle.',
      },
      noLockin: {
        title: 'Geen lock-in',
        text: 'Je content blijft van jou. Na opzegging kun je 30 dagen lang alles inzien en exporteren.',
      },
    },

    pricing: {
      title: 'Heldere prijzen, geen verrassingen',
      sub: 'Elke registratie start met 14 dagen gratis proberen op Starter-niveau.',
      periodLabel: 'Factureringsperiode',
      monthly: 'Maandelijks',
      annually: 'Jaarlijks',
      annualBadge: '2 maanden gratis',
      perMonth: '/maand',
      free: {
        name: 'Gratis',
        price: '€0',
        note: 'Voor altijd, voor kleine systemen',
        cta: 'Start gratis',
        points: ['1 gebruiker', '1 hub', '5 Figma-bestanden', 'Interactieve viewer', 'Publieke documentatiesite'],
      },
      starter: {
        name: 'Starter',
        badge: 'Meest gekozen',
        priceMonthly: '39',
        priceYearly: '32,50',
        noteYearly: '€390 per jaar gefactureerd · excl. btw',
        noteMonthly: 'Excl. btw · maandelijks opzegbaar',
        cta: 'Start 14 dagen gratis',
        points: ['3 gebruikers', '2 hubs', '50 Figma-bestanden', '5 GB opslag', 'Automatische synchronisatie', 'Alles uit Gratis'],
      },
      enterprise: {
        name: 'Enterprise',
        price: 'Op maat',
        note: 'Voor grotere organisaties',
        cta: 'Plan een gesprek',
        points: [
          'Onbeperkte gebruikers en bestanden',
          'SSO en eigen afspraken over rollen',
          'Eigen domein',
          'Verwerkersovereenkomst en SLA',
          'Persoonlijke onboarding',
        ],
      },
      guaranteesLabel: 'Garanties',
      guarantees: [
        '14 dagen gratis proberen',
        'Geen creditcard nodig',
        'Maandelijks opzegbaar',
        '30 dagen export na opzegging',
      ],
    },

    faq: {
      title: 'Veelgestelde vragen',
      items: [
        {
          q: 'Wat is Pentrical precies?',
          a: 'Pentrical is een documentatieplatform voor design systems. Het koppelt je Figma-bestanden aan geschreven richtlijnen: ontwikkelaars klikken op een design en zien direct maten, kleuren, typografie en design tokens — altijd gesynchroniseerd met de bron.',
        },
        {
          q: 'Heb ik een creditcard nodig om te starten?',
          a: 'Nee. Elke registratie start met 14 dagen gratis proberen op Starter-niveau, zonder betaalgegevens. Daarna kies je: betalen, het gratis plan, of stoppen.',
        },
        {
          q: 'Kan ik maandelijks opzeggen?',
          a: 'Ja. Het maandabonnement is per maand opzegbaar, het jaarabonnement per jaar. Na opzegging blijft je documentatie nog 30 dagen te lezen en te exporteren.',
        },
        {
          q: 'Hoe werkt de betaling?',
          a: 'Betalingen lopen via Paddle, onze merchant of record. Paddle regelt de facturatie en btw; je ontvangt automatisch een factuur per e-mail.',
        },
        {
          q: 'Wat gebeurt er met mijn data als ik stop?',
          a: 'Na het einde van je abonnement kun je 30 dagen lang alles inzien en exporteren. Daarna wordt je omgeving bewaard maar afgesloten, en uiteindelijk definitief verwijderd.',
        },
        {
          q: 'Werkt Pentrical met elk Figma-abonnement?',
          a: 'Ja. Je koppelt je eigen Figma-account via OAuth; Pentrical leest de bestanden waar jij toegang toe hebt. Er is geen Figma Enterprise-abonnement nodig.',
        },
      ],
    },

    contact: {
      title: 'Praat met ons',
      body: 'Vragen over Enterprise, een demo voor je team, of gewoon even sparren over je design system? Stuur een bericht — we reageren meestal binnen één werkdag.',
      altPrefix: 'Liever direct mailen?',
      subjects: {
        enterprise: 'Enterprise',
        demo: 'Demo aanvragen',
        support: 'Support',
        other: 'Anders',
      },
      name: 'Naam',
      email: 'E-mailadres',
      company: 'Bedrijf',
      optional: '(optioneel)',
      subject: 'Onderwerp',
      message: 'Bericht',
      website: 'Website',
      checkFields: 'Controleer de gemarkeerde velden.',
      sendFailed: 'Versturen is niet gelukt. Probeer het later opnieuw of mail {email}.',
      sending: 'Versturen…',
      send: 'Verstuur bericht',
      sentTitle: 'Bericht verzonden',
      sentBody: 'Bedankt! We reageren meestal binnen één werkdag op {email}.',
    },

    closing: {
      title: 'Probeer Pentrical 14 dagen gratis',
      body: 'Geen creditcard nodig. Binnen twee minuten heeft je team een eigen omgeving.',
      cta: 'Account aanmaken',
    },

    footer: {
      tagline: 'Levende documentatie voor design systems. Gemaakt en gehost in Europa.',
      product: 'Product',
      howItWorks: 'Hoe het werkt',
      company: 'Bedrijf',
      legal: 'Juridisch',
      terms: 'Algemene voorwaarden',
      privacy: 'Privacyverklaring',
      disclaimer: 'Disclaimer',
      rights: '© {year} Pentrical',
      madeWith: 'Betalingen via Paddle · Fonts via Bunny (EU) · Geen Google-trackers',
    },

    meta: {
      title: 'Pentrical — levende documentatie voor je design system',
      description: 'Pentrical koppelt Figma aan je design system-documentatie. Ontwikkelaars klikken op een design en zien direct specs en tokens. 14 dagen gratis, geen creditcard nodig.',
      ogDescription: 'Klikbare designs met specs, automatische Figma-sync en design tokens op één plek. Gratis te proberen.',
      ldAppDescription: 'Documentatieplatform voor design systems met automatische Figma-synchronisatie, klikbare specs en design tokens.',
      offerFree: 'Gratis',
      offerStarterMonthly: 'Starter (maandelijks)',
      offerStarterYearly: 'Starter (jaarlijks)',
    },
  },

  legal: {
    updated: 'Laatst bijgewerkt: {date}',
    // Loopt gelijk op met config/legal.php (terms_version / privacy_version)
    // in de backend; daar wordt vastgelegd wélke versie iemand aanvaardde.
    updatedDate: '31 juli 2026',
    back: '← Terug',
    navLabel: 'Juridisch',
    // Let op: '@' is vue-i18n linked-message-syntax; {'@'} rendert het letterlijk.
    // Drie gepubliceerde contactpunten (zie emailParts in LegalView.vue). Ze
    // mogen alle drie naar dezelfde postbus doorsturen; het gaat erom dat het
    // gepubliceerde adres klopt en blijft werken. AANMAKEN VÓÓR LANCERING.
    contactEmail: "support{'@'}woodmontdigital.nl",
    privacyEmail: "privacy{'@'}woodmontdigital.nl",
    securityEmail: "security{'@'}woodmontdigital.nl",
    terms: {
      title: 'Algemene voorwaarden',
      metaDescription:
        'De algemene voorwaarden voor het zakelijk gebruik van Pentrical: dienst, abonnementen, prijzen, opzegging, intellectuele eigendom, beschikbaarheid en aansprakelijkheid.',
      sections: [
        {
          h: '1. Wie we zijn en waarop deze voorwaarden van toepassing zijn',
          p: 'Pentrical is een merknaam van Woodmont Digital, gevestigd aan de Groenhovenweg 85, 2803 DB Gouda, Nederland, ingeschreven bij de Kamer van Koophandel onder nummer 97972495, btw-identificatienummer NL005300877B78. Deze voorwaarden gelden voor elk gebruik van Pentrical en vormen samen met je bestelling, de privacyverklaring en — waar van toepassing — de verwerkersovereenkomst de volledige overeenkomst tussen jouw organisatie en ons. Wie een account aanmaakt doet dat namens zijn organisatie en verklaart daartoe bevoegd te zijn; die organisatie is onze contractspartij. Eigen inkoop- of andere voorwaarden wijzen wij uitdrukkelijk van de hand.',
        },
        {
          h: '2. De dienst',
          p: 'Pentrical is een online platform voor het documenteren van design systems, met een koppeling naar Figma. Je krijgt een eigen omgeving op een subdomein waarin je team documentatie beheert en deelt. We ontwikkelen de dienst door: functies kunnen worden toegevoegd, gewijzigd of vervangen, zolang de kern van de dienst in stand blijft.',
        },
        {
          h: '3. Uitsluitend zakelijk gebruik',
          p: 'Pentrical wordt uitsluitend aangeboden aan ondernemingen, instellingen en personen die handelen in de uitoefening van een beroep of bedrijf. Door een account aan te maken verklaar je zakelijk te handelen en niet als consument. Consumentenrechten, waaronder het wettelijke herroepingsrecht bij overeenkomsten op afstand, zijn daarom niet van toepassing. Alle op de website vermelde prijzen zijn exclusief btw.',
        },
        {
          h: '4. Account, proefperiode en gratis plan',
          p: 'Elke registratie start met een gratis proefperiode van veertien dagen, zonder betaalgegevens. Daarna kies je een betaald abonnement of het gratis plan. Doe je niets, dan wordt je omgeving alleen-lezen en dertig dagen later definitief verwijderd; je ontvangt vooraf een waarschuwing per e-mail. Het gratis plan kent beperkingen in het aantal gebruikers en bestanden zoals vermeld op de website. We mogen het gratis plan wijzigen of beëindigen met een aankondiging van ten minste dertig dagen, zodat je kunt overstappen of je gegevens kunt exporteren.',
        },
        {
          h: '5. Abonnementen en betaling',
          p: 'Betaalde abonnementen worden afgerekend via Paddle, onze merchant of record. Paddle verzorgt de facturatie en de btw-afdracht en treedt bij de betaling op als verkopende partij; op die transactie zijn daarnaast de voorwaarden van Paddle van toepassing. Abonnementen worden vooraf betaald en verlengen automatisch per maand respectievelijk per jaar, tenzij je opzegt. Opzeggen kan op elk moment via je accountinstellingen en gaat in aan het einde van de lopende betaalperiode; die periode loopt gewoon uit. Bij een mislukte betaling mogen we de toegang opschorten nadat we je daarover hebben bericht.',
        },
        {
          h: '6. Prijswijzigingen',
          p: 'We mogen onze prijzen wijzigen. Een wijziging kondigen we ten minste dertig dagen van tevoren per e-mail aan bij de accounteigenaar en gaat in bij de eerstvolgende verlenging; voor lopende jaarabonnementen dus pas bij de volgende jaarlijkse verlenging. Ben je het niet eens met de nieuwe prijs, dan kun je vóór de ingangsdatum opzeggen tegen het einde van de lopende betaalperiode. Blijf je daarna gebruikmaken van de dienst, dan geldt de nieuwe prijs.',
        },
        {
          h: '7. Opzegging, bewaartermijn en export',
          p: 'Na afloop van een opgezegd abonnement blijft je omgeving dertig dagen alleen-lezen beschikbaar, zodat je je gegevens kunt exporteren. Daarna wordt de omgeving gesloten en nog negentig dagen bewaard; uiterlijk honderdtwintig dagen na het einde van het abonnement worden alle gegevens definitief verwijderd. Vóór die definitieve verwijdering ontvang je een waarschuwing per e-mail. Sluit je binnen die termijn opnieuw een abonnement af, dan wordt de omgeving volledig hersteld. Een verlopen proefaccount wordt dertig dagen na afloop van de proefperiode definitief verwijderd.',
        },
        {
          h: '8. Restituties',
          p: 'Betaalde bedragen worden niet gerestitueerd, ook niet bij tussentijdse opzegging; de gratis proefperiode is bedoeld om de dienst vooraf te beoordelen. Werkt er iets niet zoals afgesproken, neem dan contact op via %EMAIL% — we zoeken dan samen naar een passende oplossing. Je wettelijke rechten bij een tekortkoming van onze kant blijven onverlet.',
        },
        {
          h: '9. Intellectuele eigendom',
          p: 'Alle rechten op het platform zelf — software, vormgeving, documentatie en de merknaam Pentrical — berusten bij Woodmont Digital of haar licentiegevers. Voor de duur van de overeenkomst krijg je een niet-exclusief, niet-overdraagbaar recht om de dienst binnen je eigen organisatie te gebruiken. Alle rechten op de inhoud die je in je omgeving plaatst blijven volledig bij jou of je licentiegevers. Je verleent ons uitsluitend het beperkte recht om die inhoud op te slaan, te kopiëren, te verwerken en te tonen voor zover dat nodig is om de dienst te leveren, te beveiligen en van back-ups te voorzien. Dat recht eindigt zodra je gegevens worden verwijderd.',
        },
        {
          h: '10. Jouw inhoud en verantwoordelijkheden',
          p: 'Je bent verantwoordelijk voor de inhoud die je in je omgeving plaatst en garandeert dat je over de benodigde rechten beschikt. Je houdt inloggegevens vertrouwelijk en bent verantwoordelijk voor wat er onder jouw accounts gebeurt. Niet toegestaan is: inbreuk maken op rechten van derden, onrechtmatige of strafbare inhoud plaatsen, de dienst of de infrastructuur verstoren of overbelasten, beveiligingsmaatregelen omzeilen, of de dienst doorverkopen dan wel beschikbaar stellen aan derden buiten je eigen organisatie. Je vrijwaart ons tegen aanspraken van derden die voortvloeien uit jouw inhoud of uit gebruik in strijd met deze voorwaarden.',
        },
        {
          h: '11. Concurrerend gebruik',
          p: 'Je mag de dienst — inclusief de exportfunctie en, voor zover beschikbaar, een API — niet gebruiken, en niet via een derde laten gebruiken, om een concurrerend product te bouwen of te vermarkten, om Pentrical te benchmarken of anderszins competitief te analyseren, of om op geautomatiseerde of grootschalige wijze gegevens te onttrekken buiten de reguliere exportfunctie om. Voor een integratie of tool die er hoofdzakelijk op is gericht gegevens vanuit Pentrical over te zetten naar een concurrerend platform is onze voorafgaande schriftelijke toestemming vereist. Dit artikel beperkt niet je eigen recht uit artikel 9 om je eigen inhoud te gebruiken, te exporteren en mee te nemen naar een andere dienst: het richt zich tegen het bouwen van tooling tegen Pentrical, niet tegen jouw vrijheid om als klant over te stappen.',
        },
        {
          h: '12. Persoonsgegevens en verwerkersovereenkomst',
          p: 'Voor de gegevens die wij zelf van jou verwerken — accountgegevens, facturatie en logbestanden — zijn wij verwerkingsverantwoordelijke; hoe we daarmee omgaan staat in onze privacyverklaring. Bevat de inhoud die je in je omgeving plaatst persoonsgegevens, dan ben jij daarvoor verwerkingsverantwoordelijke en zijn wij verwerker. Op die verwerking is onze verwerkersovereenkomst van toepassing, die je op verzoek via %EMAIL_PRIVACY% ontvangt en die integraal deel uitmaakt van deze overeenkomst.',
        },
        {
          h: '13. Diensten van derden',
          p: 'De dienst maakt gebruik van diensten van derden, waaronder Figma voor het synchroniseren van ontwerpen, Paddle voor betalingen en Brevo voor e-mail. De koppeling met Figma komt tot stand met jouw eigen Figma-account en op basis van jouw overeenkomst met Figma; wij hebben geen invloed op hun voorwaarden, tarieven, beschikbaarheid of wijzigingen in hun API. Wijzigt of beëindigt een derde zijn dienst, dan mogen wij de betrokken functionaliteit aanpassen of stopzetten. Wij zijn niet aansprakelijk voor uitval of wijzigingen bij deze derden; bij een wezenlijke wijziging informeren we je en kun je opzeggen tegen het einde van de lopende betaalperiode.',
        },
        {
          h: '14. Beschikbaarheid en onderhoud',
          p: 'We spannen ons in om de dienst continu beschikbaar te houden, maar geven geen gegarandeerde beschikbaarheid: er geldt geen SLA. Voor onderhoud kan de dienst tijdelijk onbereikbaar zijn; gepland onderhoud kondigen we waar mogelijk vooraf aan en voeren we zoveel mogelijk buiten kantooruren uit. Ondersteuning bieden we per e-mail op werkdagen, zonder gegarandeerde reactietijd.',
        },
        {
          h: '15. Opschorting en beëindiging',
          p: 'We mogen de toegang tot een account opschorten bij achterstallige betaling, bij een reëel beveiligingsrisico, of bij handelen in strijd met artikel 10 of 11. Behalve wanneer onmiddellijk ingrijpen nodig is, waarschuwen we je eerst en krijg je een redelijke termijn om de situatie te herstellen. Blijft herstel uit, dan mogen we de overeenkomst schriftelijk beëindigen; de bewaar- en exporttermijnen uit artikel 7 blijven dan gelden, tenzij verwijdering wettelijk verplicht is.',
        },
        {
          h: '16. Aansprakelijkheid',
          p: 'Onze aansprakelijkheid is per gebeurtenis en per kalenderjaar beperkt tot het bedrag dat je in de twaalf maanden vóór de schadeveroorzakende gebeurtenis voor de dienst hebt betaald, met een minimum van honderd euro. We zijn niet aansprakelijk voor indirecte schade, waaronder gederfde winst, gemiste besparingen, reputatieschade, verlies van gegevens en bedrijfsstilstand. Deze beperkingen gelden niet bij opzet of bewuste roekeloosheid van onze kant. Een aanspraak vervalt als je die niet binnen twaalf maanden na ontdekking van de schade schriftelijk bij ons meldt. Het maken van eigen back-ups van je inhoud blijft jouw verantwoordelijkheid: wij maken back-ups voor onze eigen bedrijfsvoering, niet als archiveringsdienst voor jou.',
        },
        {
          h: '17. Overmacht',
          p: 'Bij overmacht zijn we niet gehouden onze verplichtingen na te komen. Daaronder valt in elk geval: uitval van hosting, netwerk of stroomvoorziening, storingen bij toeleveranciers als bedoeld in artikel 13, cyberaanvallen, overheidsmaatregelen en arbeidsconflicten. Duurt de overmacht langer dan dertig dagen, dan mogen beide partijen de overeenkomst beëindigen zonder schadeplichtig te worden.',
        },
        {
          h: '18. Wijziging van deze voorwaarden',
          p: 'We mogen deze voorwaarden wijzigen. Wezenlijke wijzigingen kondigen we ten minste dertig dagen van tevoren per e-mail aan bij de accounteigenaar. Ben je het niet eens met een wezenlijke wijziging, dan kun je vóór de ingangsdatum opzeggen tegen het einde van de lopende betaalperiode. Bij registratie leggen we vast welke versie van deze voorwaarden je hebt aanvaard.',
        },
        {
          h: '19. Toepasselijk recht en geschillen',
          p: 'Op deze overeenkomst is uitsluitend Nederlands recht van toepassing; het Weens Koopverdrag is niet van toepassing. Geschillen leggen we voor aan de bevoegde rechter van de rechtbank Den Haag, onverminderd ons recht je aan te spreken bij de rechter van jouw vestigingsplaats. De Nederlandse tekst van deze voorwaarden is leidend; vertalingen zijn uitsluitend ter informatie.',
        },
      ],
    },
    privacy: {
      title: 'Privacyverklaring',
      metaDescription:
        'Hoe Pentrical met je gegevens omgaat: wat we verwerken, op welke grondslag, welke verwerkers we inschakelen, doorgifte buiten de EER, bewaartermijnen, cookies en je rechten.',
      sections: [
        {
          h: '1. Wie we zijn',
          p: 'Pentrical is een merknaam van Woodmont Digital, gevestigd aan de Groenhovenweg 85, 2803 DB Gouda, Nederland, ingeschreven bij de Kamer van Koophandel onder nummer 97972495, btw-identificatienummer NL005300877B78. Voor vragen over privacy of het uitoefenen van je rechten mail je %EMAIL_PRIVACY%. We hebben geen functionaris voor gegevensbescherming aangesteld; dat is voor een organisatie van deze omvang en met deze verwerkingen niet wettelijk verplicht.',
        },
        {
          h: '2. Onze rol: verantwoordelijke én verwerker',
          p: 'Voor de gegevens die we nodig hebben om de dienst te leveren en te factureren — accountgegevens, betaalgegevens en technische logbestanden — zijn wij verwerkingsverantwoordelijke. Deze verklaring gaat over die gegevens. Voor de inhoud die klanten zelf in hun omgeving plaatsen zijn wij verwerker: bevat die inhoud persoonsgegevens, dan bepaalt de klant het doel en de middelen en voeren wij die verwerking alleen uit. Daarvoor geldt onze verwerkersovereenkomst, die klanten op verzoek ontvangen.',
        },
        {
          h: '3. Welke gegevens we verwerken',
          p: 'Accountgegevens: naam, e-mailadres, versleuteld opgeslagen wachtwoord, de naam van je omgeving, je rol in het team en je voorkeuren voor taal, weergave en meldingen. Registratiegegevens: het moment waarop je de voorwaarden hebt aanvaard, de versie daarvan en het IP-adres van dat moment. Gebruiksgegevens: een activiteitenlogboek van welke handeling wanneer door welke gebruiker is verricht, inclusief IP-adres en browsertype, en het aantal mislukte inlogpogingen. Betaalgegevens: naam, factuuradres, land en btw-nummer voor zover Paddle die aan ons terugkoppelt — je kaart- of bankgegevens zien wij niet. Inhoud: de documentatie, ontwerpen en previews die je zelf plaatst of via je eigen Figma-koppeling synchroniseert. Daarnaast technische logbestanden van de server, die tijdelijk IP-adressen en foutmeldingen kunnen bevatten. Naam en e-mailadres zijn nodig om een account te kunnen aanmaken; zonder die gegevens kunnen we de dienst niet leveren.',
        },
        {
          h: '4. Doeleinden en grondslagen',
          p: 'Om de overeenkomst uit te voeren (artikel 6 lid 1 sub b AVG) maken we je account aan, maken we inloggen mogelijk, tonen we je omgeving, synchroniseren we met Figma en sturen we service-e-mails zoals verificatie, waarschuwingen over je abonnement en herinneringen vóór verwijdering. Op grond van een gerechtvaardigd belang (artikel 6 lid 1 sub f AVG) houden we een activiteitenlogboek bij en registreren we mislukte inlogpogingen, om misbruik te voorkomen en incidenten te kunnen onderzoeken; ons belang bij een veilig en herleidbaar platform weegt hier op tegen de beperkte inbreuk op je privacy. Facturatie en het bewaren van onze administratie doen we om te voldoen aan een wettelijke verplichting (artikel 6 lid 1 sub c AVG). We verkopen geen gegevens en gebruiken ze niet voor advertenties of profilering.',
        },
        {
          h: '5. Verwerkers en doorgifte buiten de EER',
          p: 'Hosting: Hetzner Online GmbH (Duitsland) — alle servers en databases staan binnen de EU. E-mail: Brevo, Sendinblue SAS (Frankrijk). Betalingen: Paddle.com Market Ltd (Verenigd Koninkrijk) als merchant of record; voor het Verenigd Koninkrijk geldt een adequaatheidsbesluit van de Europese Commissie. Lettertypen: Bunny Fonts, BunnyWay d.o.o. (Slovenië), dat geen cookies plaatst en geen bezoekgegevens bewaart. DNS en certificaten: Cloudflare. Koppel je een Figma-bestand, dan worden je Figma-toegangstoken en de opgehaalde ontwerpgegevens uitgewisseld met Figma, Inc. in de Verenigde Staten; die doorgifte berust op het EU-US Data Privacy Framework en, voor zover nodig, op de modelcontractbepalingen van de Europese Commissie. Met al onze verwerkers zijn verwerkersovereenkomsten gesloten. Buiten het hierboven genoemde vindt geen doorgifte buiten de Europese Economische Ruimte plaats.',
        },
        {
          h: '6. Bewaartermijnen',
          p: 'Je gegevens blijven bewaard zolang je account bestaat. Verloopt de proefperiode zonder abonnement, dan wordt je omgeving alleen-lezen en dertig dagen later definitief verwijderd — in totaal vierenveertig dagen na registratie. Zeg je een betaald abonnement op, dan blijft je omgeving dertig dagen alleen-lezen beschikbaar om te exporteren, wordt daarna gesloten en nog negentig dagen bewaard, en uiterlijk honderdtwintig dagen na het einde van het abonnement definitief verwijderd. Technische logbestanden van de server bewaren we veertien dagen. Het activiteitenlogboek bestaat zolang de omgeving bestaat en wordt bij verwijdering van de omgeving mee verwijderd. Facturatiegegevens bewaart Paddle als merchant of record; wij bewaren onze administratie zeven jaar op grond van de fiscale bewaarplicht.',
        },
        {
          h: '7. Cookies en lokale opslag',
          p: 'Pentrical gebruikt geen tracking- of advertentiecookies en meet geen bezoekersgedrag. We plaatsen alleen wat nodig is om de dienst te laten werken: een sessiecookie om je ingelogd te houden en een beveiligingstoken dat misbruik van formulieren voorkomt. Bezoekers van een wachtwoordbeveiligde documentatieomgeving krijgen, ná het invullen van het juiste wachtwoord, een vergelijkbaar cookie dat onthoudt welke onderdelen al zijn ontgrendeld; dat cookie bevat geen persoonsgegevens en is, afhankelijk van je keuze, een browsersessie of dertig dagen geldig. Daarnaast slaan we in je browser je eigen instellingen op — taal, thema en weergavevoorkeuren — zodat je die niet elke keer opnieuw hoeft te kiezen. Voor deze cookies is geen toestemming vereist; je kunt ze via je browserinstellingen verwijderen, maar dan werkt inloggen — of het ontgrendelen van afgeschermde documentatie — niet meer. Op de facturatiepagina laadt Paddle een eigen script om de betaling af te handelen; dat gebeurt alleen als je die pagina zelf opent. Onze lettertypen komen van Bunny Fonts, dat geen cookies plaatst en geen bezoekgegevens bewaart.',
        },
        {
          h: '8. Jouw rechten',
          p: 'Je hebt recht op inzage, correctie, verwijdering, beperking van de verwerking, bezwaar tegen verwerkingen op grond van een gerechtvaardigd belang, en overdraagbaarheid van je gegevens. Je naam en je voorkeuren pas je zelf aan in je profiel; voor een wijziging van je e-mailadres, voor inzage, voor een volledige export of voor verwijdering van je account mail je %EMAIL_PRIVACY%. We reageren binnen één maand. Wordt je account verwijderd, dan verdwijnt ook de inhoud van je omgeving — vraag vooraf een export aan als je die wilt bewaren. Ben je niet tevreden over hoe we met je gegevens omgaan, dan kun je een klacht indienen bij de Autoriteit Persoonsgegevens.',
        },
        {
          h: '9. Beveiliging en datalekken',
          p: 'Verbindingen zijn versleuteld met TLS, wachtwoorden worden gehasht opgeslagen en omgevingen van klanten zijn strikt van elkaar gescheiden; die scheiding wordt bij elke wijziging automatisch getest. Toegang tot productiesystemen is beperkt tot wie die nodig heeft. Doet zich een datalek voor met een risico voor je rechten en vrijheden, dan melden we dat binnen tweeënzeventig uur bij de Autoriteit Persoonsgegevens en informeren we jou zonder onnodige vertraging wanneer het lek jou raakt. Denk je een kwetsbaarheid gevonden te hebben, meld het dan via %EMAIL_SECURITY%.',
        },
        {
          h: '10. Geen geautomatiseerde besluitvorming',
          p: 'We nemen geen besluiten met rechtsgevolgen of vergelijkbare gevolgen op basis van uitsluitend geautomatiseerde verwerking, en we doen niet aan profilering.',
        },
        {
          h: '11. Wijzigingen in deze verklaring',
          p: 'We kunnen deze privacyverklaring aanpassen; de datum bij deze verklaring geeft aan wanneer dat voor het laatst gebeurde. Bij wezenlijke wijzigingen informeren we accounteigenaren per e-mail.',
        },
      ],
    },
    disclaimer: {
      title: 'Disclaimer',
      metaDescription:
        'De disclaimer voor pentrical.com: reikwijdte, informatie op deze website, inhoud van klanten, handelsmerken en aansprakelijkheid.',
      sections: [
        {
          h: '1. Reikwijdte',
          p: 'Deze disclaimer geldt voor de openbare website pentrical.com en voor informatie die daar wordt getoond. Heb je een actief abonnement, dan gaan de algemene voorwaarden voor zover ze hetzelfde onderwerp regelen; deze disclaimer beperkt geen van de daarin toegezegde rechten.',
        },
        {
          h: '2. Informatie op deze website',
          p: 'We houden de informatie op deze website — functiebeschrijvingen, schermafbeeldingen, prijzen — naar beste weten actueel, maar geven geen garantie dat alles op elk moment volledig, juist of bijgewerkt is. Wat je daadwerkelijk krijgt, staat in je bestelling en de algemene voorwaarden; bij verschil gaan die voor.',
        },
        {
          h: '3. Inhoud die klanten zelf plaatsen',
          p: 'Pentrical host documentatie die klanten zelf schrijven, samenstellen en beheren in hun eigen omgeving. We beoordelen, controleren of onderschrijven die inhoud niet en zijn niet verantwoordelijk voor de juistheid, volledigheid of rechtmatigheid ervan — ook niet waar het gaat om toegankelijkheids- of designrichtlijnen. Meen je dat ergens onrechtmatige inhoud staat, meld dat dan via %EMAIL_SECURITY%.',
        },
        {
          h: '4. Geen professioneel advies',
          p: 'Niets op deze website of in via Pentrical gehoste documentatie is bedoeld of geschikt als juridisch, compliance- of ander professioneel advies — ook niet waar het toegankelijkheid betreft, zoals WCAG of de Europese Toegankelijkheidsakte. Raadpleeg voor dergelijke vragen een deskundige.',
        },
        {
          h: '5. Handelsmerken',
          p: "Figma is een handelsmerk van Figma, Inc. Pentrical noemt die naam uitsluitend om de koppeling met Figma te beschrijven; Pentrical is een zelfstandig bedrijf en is niet verbonden aan, gesponsord door of goedgekeurd door Figma, Inc. Andere op deze website genoemde namen en logo's zijn eigendom van hun respectievelijke houders.",
        },
        {
          h: '6. Externe links',
          p: 'Deze website bevat links naar diensten van derden, waaronder Figma. Die links zijn voor het gemak opgenomen; we hebben geen zeggenschap over en aanvaarden geen verantwoordelijkheid voor de inhoud of het privacybeleid van die externe sites.',
        },
        {
          h: '7. Aansprakelijkheid',
          p: 'Voor zover de wet dat toestaat is het gebruik van deze website voor eigen risico. Voor klanten met een actief abonnement blijft de aansprakelijkheidsregeling uit artikel 16 van de algemene voorwaarden onverkort van toepassing; deze disclaimer vervangt of beperkt die regeling niet.',
        },
      ],
    },
  },

  auth: {
    common: {
      errorSummaryTitle: 'Er zijn fouten opgetreden',
      emailLabel: 'E-mailadres',
      emailPlaceholder: "naam{'@'}voorbeeld.nl",
      passwordLabel: 'Wachtwoord',
      showPassword: 'Wachtwoord tonen',
      hidePassword: 'Wachtwoord verbergen',
      passwordHint: 'Minimaal 8 tekens',
      emailRequired: 'E-mailadres is verplicht',
      emailInvalid: 'Voer een geldig e-mailadres in',
      passwordRequired: 'Wachtwoord is verplicht',
      passwordMin: 'Wachtwoord moet minimaal 8 tekens zijn',
      // Boven het formulier, vóór het eerste veld: wie invult hoort niet
      // pas bij het verzenden te ontdekken dat er verplichte velden zijn.
      requiredNote: 'Velden met een asterisk zijn verplicht.',
    },
    brand: {
      headline: 'Je design system, gedocumenteerd en gedeeld.',
      subline: 'Eén plek voor tokens, componenten en Figma — voor je hele team.',
      footer: '© Pentrical',
    },
    login: {
      title: 'Inloggen bij Pentrical',
      submit: 'Inloggen',
      submitting: 'Bezig met inloggen...',
      successAnnounce: 'Inloggen geslaagd. U wordt doorgestuurd.',
      failed: 'Inloggen mislukt',
      forgotPassword: 'Wachtwoord vergeten?',
      noEnvironmentAccess: 'Je hebt geen toegang tot deze omgeving. Controleer het webadres, of log in bij de omgeving waar je lid van bent.',
      checkEnvironmentAddress: 'Klopt het webadres van deze omgeving? Hoor je bij een andere omgeving, log daar dan in.',
      redirecting: 'Je wordt ingelogd bij {environment}…',
      chooseEnvironment: 'Waar wil je heen?',
      chooseEnvironmentHint: 'Je hoort bij meerdere omgevingen. Kies er een om verder te gaan.',
      noEnvironments: 'Je bent ingelogd, maar hebt nog geen omgeving. Vraag een teambeheerder om je uit te nodigen, of maak er zelf een aan.',
      noAccount: 'Nog geen account?',
      createFree: 'Maak er gratis een aan',
    },
    handoff: {
      loadingTitle: 'Je wordt ingelogd…',
      loadingBody: 'Een moment, we openen je omgeving.',
      errorTitle: 'Inloggen lukte niet',
      failed: 'Deze inloglink is niet meer geldig. Log opnieuw in.',
      missingCode: 'Deze inloglink is onvolledig. Log opnieuw in.',
      backToLogin: 'Terug naar inloggen',
    },
    forgot: {
      title: 'Wachtwoord vergeten?',
      intro: 'Vul je e-mailadres in, dan sturen we je een link om een nieuw wachtwoord te kiezen.',
      submit: 'Verstuur resetlink',
      submitting: 'Versturen…',
      failed: 'De resetlink kon niet worden verstuurd. Probeer het opnieuw.',
      rateLimited: 'Te veel pogingen. Wacht een minuut en probeer het opnieuw.',
      backToLogin: 'Terug naar inloggen',
      success: {
        title: 'Controleer je e-mail',
        bodyPre: 'Als er een account bestaat voor ',
        bodyPost: ', hebben we zojuist een link gestuurd om je wachtwoord te herstellen.',
        hint: 'Na een paar minuten nog geen mail? Kijk in je spamfolder, of controleer of je het adres gebruikt waarmee je je hebt geregistreerd.',
      },
    },
    reset: {
      title: 'Kies een nieuw wachtwoord',
      introPre: 'Stel een nieuw wachtwoord in voor ',
      introPost: '.',
      newPasswordLabel: 'Nieuw wachtwoord',
      confirmLabel: 'Bevestig nieuw wachtwoord',
      submit: 'Wachtwoord herstellen',
      submitting: 'Herstellen…',
      failed: 'Je wachtwoord kon niet worden hersteld. Probeer het opnieuw.',
      mismatch: 'De wachtwoorden komen niet overeen',
      invalidTitle: 'Ongeldige resetlink',
      invalidBody: 'Deze link is onvolledig of verlopen. Vraag een nieuwe aan om verder te gaan.',
      requestNew: 'Nieuwe link aanvragen',
      success: {
        title: 'Wachtwoord hersteld',
        body: 'Je wachtwoord is gewijzigd. Je kunt nu inloggen met je nieuwe wachtwoord.',
        toLogin: 'Ga naar de inlogpagina',
      },
    },
    signup: {
      title: 'Account aanmaken',
      subtitle: 'Probeer 14 dagen gratis - geen creditcard nodig',
      accountNameLabel: 'Bedrijfs- of teamnaam',
      accountNamePlaceholder: 'Acme BV',
      accountNameRequired: 'Bedrijfs- of teamnaam is verplicht',
      subdomainLabel: 'Adres van je omgeving',
      subdomainPlaceholder: 'acme',
      subdomainRequired: 'Subdomein is verplicht',
      subdomainInvalid:
        'Minimaal 2 tekens; alleen kleine letters, cijfers en koppeltekens (niet aan de randen)',
      subdomainHint: 'Hier vinden jij en je team de documentatie straks terug.',
      nameLabel: 'Je naam',
      namePlaceholder: 'Voor- en achternaam',
      nameRequired: 'Je naam is verplicht',
      consent: {
        business:
          'Pentrical is een zakelijke dienst: je maakt dit account aan namens je organisatie. Alle prijzen zijn exclusief btw.',
        termsPre: 'Ik ga namens mijn organisatie akkoord met de ',
        termsLink: 'algemene voorwaarden',
        termsPost: '.',
        termsRequired: 'Je moet akkoord gaan met de algemene voorwaarden om verder te gaan',
        // Bewust "gelezen" en niet "akkoord": een privacyverklaring is
        // informatie (AVG art. 13), geen toestemming. De grondslag is de
        // overeenkomst; wie het als toestemming presenteert, moet die ook
        // net zo makkelijk kunnen laten intrekken — en dan valt de dienst om.
        privacyPre: 'Ik heb de ',
        privacyLink: 'privacyverklaring',
        privacyPost: ' gelezen.',
        privacyRequired: 'Bevestig dat je de privacyverklaring hebt gelezen om verder te gaan',
      },
      captcha: {
        pending: 'De beveiligingscontrole is nog bezig. Wacht even en probeer het opnieuw.',
      },
      submit: 'Account aanmaken',
      submitting: 'Account wordt aangemaakt...',
      rateLimited: 'Te veel pogingen. Wacht even een minuut en probeer het opnieuw.',
      genericServerError: 'Er ging iets mis bij het aanmaken van je account. Probeer het opnieuw.',
      createdAnnounce: 'Controleer je e-mail.',
      haveAccount: 'Al een account?',
      login: 'Inloggen',
      success: {
        title: 'Kijk in je mailbox',
        followLink: 'Volg de link in de mail om verder te gaan.',
        sentToPre: 'We hebben een bericht gestuurd naar',
        sentToMid: '.',
        noEmail: 'Geen mail ontvangen? Controleer je spam-map.',
        toLogin: 'Naar de inlogpagina',
      },
    },
    invite: {
      loadError: 'Deze uitnodiging kon niet geladen worden.',
      loading: 'Uitnodiging laden…',
      invalidTitle: 'Uitnodiging niet geldig',
      toLogin: 'Naar inloggen',
      joinTitle: 'Word lid van {account}',
      invitedByText: '{name} nodigt je uit',
      invitedGenericText: 'Je bent uitgenodigd',
      collaborateIn: 'om samen te werken in',
      nameLabel: 'Naam',
      passwordExisting: 'Je wachtwoord',
      passwordNew: 'Kies een wachtwoord',
      passwordHint: 'Minimaal 8 tekens.',
      passwordMinError: 'Vul een wachtwoord van minimaal 8 tekens in.',
      nameRequiredError: 'Vul je naam in.',
      acceptFailed: 'Accepteren mislukt. Controleer je gegevens en probeer het opnieuw.',
      submitExisting: 'Accepteren en inloggen',
      submitNew: 'Account aanmaken en toetreden',
    },
  },

  /*
   * De meldingsbalk van Pentrical zelf, bovenin het venster. Alleen de
   * omlijsting is vertaald: de tekst van de melding wordt door de
   * platformbeheerder ingetypt en gaat onvertaald naar alle hubs.
   */
  appVersion: {
    // Bewust Engels in beide talen: dit is een bericht van Pentrical zelf.
    message: 'A new version of Pentrical is available.',
    reload: 'Reload the page',
    dismiss: 'Dismiss',
  },
  platformAlerts: {
    variants: {
      // Alleen voor schermlezers: kleur mag niet de enige drager van
      // betekenis zijn (WCAG 1.4.1).
      information: 'Melding',
      attention: 'Let op',
      emergency: 'Storing',
    },
    dismiss: 'Melding sluiten',
    defaultLinkLabel: 'Meer informatie',
  },

  chrome: {
    adminBar: {
      label: 'Beheerbalk',
      viewAsVisitor: 'Bekijk als bezoeker',
      viewAsVisitorTitle: 'Voorbeeld: zo ziet een bezoeker de site',
      settings: 'Instellingen',
      openUserMenu: 'Gebruikersmenu openen',
      myAccount: 'Mijn account',
      gettingStarted: 'Aan de slag',
      help: 'Help',
      logout: 'Uitloggen',
    },
    preview: {
      banner: 'Voorbeeldweergave — dit is exact wat een bezoeker ziet.',
      back: 'Terug naar beheer',
    },
    sidebar: {
      label: 'Documentatie-navigatie',
      docs: 'Documentatie',
      expand: 'Navigatie uitklappen',
      collapse: 'Navigatie inklappen',
      resizeHandle: 'Breedte van de navigatie aanpassen (pijltjestoetsen; Enter klapt in)',
      loading: 'Navigatie laden…',
      loadFailed: 'Kon de navigatie niet laden',
      close: 'Navigatie sluiten',
    },
    mainLabel: 'Hoofdinhoud',
  },

  nav: {
    // Hoofdnavigatie / categorie-acties (TheHeader)
    openMenu: 'Menu openen',
    closeMenu: 'Menu sluiten',
    newCategory: 'Nieuwe categorie',
    emptyEditor: 'Nog geen categorieën. Maak er een met "Nieuwe categorie" bovenin.',
    emptyVisitor: 'Hier staat nog geen inhoud.',
    emptyCategoryHint: "Nog geen pagina's",
    newCategoryPlaceholder: 'Naam nieuwe categorie…',
    newCategoryNameLabel: 'Naam nieuwe categorie',
    renameCategoryLabel: 'Nieuwe categorienaam',
    actionsFor: 'Acties voor {name}',
    rename: 'Hernoemen',
    hideFromVisitors: 'Verbergen voor bezoekers',
    makeVisible: 'Zichtbaar maken',
    access: 'Toegang…',
    moveLeft: 'Naar links',
    moveRight: 'Naar rechts',
    moveToCategory: 'Verplaats naar categorie',
    cannotMoveHasSubcategories: 'Niet mogelijk: dit item heeft subcategorieën',
    noOtherCategories: 'Geen andere categorieën',
    deleteWithContent: 'Verwijderen (incl. inhoud)',
    // Documentatieboom (SidebarNav)
    newNameLabel: 'Nieuwe naam',
    newTitleLabel: 'Nieuwe titel',
    addToLabel: 'Toevoegen aan {name}',
    addPageOrSubcategory: 'Pagina of subcategorie toevoegen',
    addPageToLabel: 'Pagina toevoegen aan {name}',
    addPageTitle: 'Pagina toevoegen',
    draft: 'Concept',
    newPage: 'Nieuwe pagina',
    newSubcategory: 'Nieuwe subcategorie',
    newSubcategoryPlaceholder: 'Naam nieuwe subcategorie…',
    newSubcategoryLabel: 'Naam nieuwe subcategorie',
    newPagePlaceholder: 'Naam nieuwe pagina…',
    newPageLabel: 'Naam nieuwe pagina',
    duplicate: 'Dupliceren',
    moveUp: 'Omhoog',
    moveDown: 'Omlaag',
    moveTo: 'Verplaats naar',
    topLevel: 'Hoofdniveau',
    noOtherLocations: 'Geen andere locaties',
    delete: 'Verwijderen',
    // Meldingen / bevestigingen
    saveFailed: 'Opslaan mislukt',
    visibilityFailed: 'Zichtbaarheid wijzigen mislukt',
    moveFailed: 'Verplaatsen mislukt',
    deleteFailed: 'Verwijderen mislukt',
    duplicateFailed: 'Dupliceren mislukt',
    categoryMovedTo: 'Categorie "{name}" verplaatst naar positie {position} van {total}',
    movedToCategory: '"{name}" verplaatst naar categorie "{destination}"',
    categoryDeleted: 'Categorie "{name}" verwijderd',
    confirmDeleteCategory:
      'Weet je zeker dat je "{name}" wilt verwijderen? Alle pagina\'s en subcategorieën hierin worden ook verwijderd. Dit kan niet ongedaan worden gemaakt.',
    confirmDeletePageWithTabs:
      'Weet je zeker dat je "{name}" wilt verwijderen? De {count} tab(s) van deze pagina worden ook verwijderd. Dit kan niet ongedaan worden gemaakt.',
    confirmDeletePage: 'Weet je zeker dat je "{name}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
  },

  account: {
    title: 'Mijn account',
    nav: {
      profile: 'Profiel',
      hubs: 'Hubs',
      team: 'Team',
      communication: 'Communicatie',
      accessibility: 'Toegankelijkheid',
      billing: 'Abonnement',
      about: 'Over',
    },
    common: {
      save: 'Opslaan',
      prefsSaved: 'Voorkeuren opgeslagen',
      saveFailed: 'Opslaan mislukt',
      loadFailed: 'Laden mislukt',
      loading: 'Bezig met laden…',
      tryAgainLater: 'Probeer het later opnieuw.',
      cancel: 'Annuleren',
    },
    hubs: {
      beschikbaarheid: {
        vrij: 'Dit adres is vrij.',
        bezet: 'Dit adres is al in gebruik. Kies een andere naam.',
        gereserveerd: 'Dit adres is gereserveerd voor de applicatie zelf.',
        ongeldig: 'Gebruik alleen kleine letters, cijfers en streepjes.',
      },
      title: 'Hubs',
      subtitle: 'De documentatiesites van dit account',
      loading: 'Hubs laden…',
      loadError: 'Kon de hubs niet ophalen',
      countLimited: '{count} van {limit} hubs in gebruik',
      countUnlimited: '{count} hubs — je plan kent geen maximum',
      primaryBadge: 'Hoofdadres',
      currentBadge: 'Je werkt hier',
      contentSummary: '{sections} categorieën · {pages} pagina\u2019s',
      switchTo: 'Hierheen',
      makePrimary: 'Op het hoofdadres zetten',
      makePrimaryFor: '{name} op het hoofdadres zetten',
      renameFor: '{name} bewerken',
      deleteFor: '{name} verwijderen',
      newHub: 'Nieuwe hub',
      newHeading: 'Nieuwe hub',
      editHeading: 'Hub bewerken',
      nameLabel: 'Naam',
      slugLabel: 'Adres',
      switchFailedTitle: 'Wisselen is niet gelukt',
      switchFailedBody: 'Je login kon niet worden meegenomen naar het andere adres. Hier ben je nog gewoon ingelogd; probeer het opnieuw.',
      slugHint: 'Komt in het webadres te staan. Alleen kleine letters, cijfers en streepjes.',
      save: 'Opslaan',
      saved: 'Hub opgeslagen',
      saveFailed: 'Opslaan mislukt',
      primaryChanged: 'Hoofdadres gewijzigd',
      limitReached: 'Je plan bevat {limit} hub(s) — je gebruikt ze allemaal.',
      limitReachedLink: 'Bekijk de plannen',
      trashHeading: 'Prullenbak',
      trashIntro: 'Verwijderde hubs blijven {days} dagen bewaard en zijn tot die tijd terug te halen.',
      purgeOn: 'wordt verwijderd op {date}',
      restore: 'Terughalen',
      restored: 'Hub teruggehaald',
      restoredFromMail: 'Deze documentatiesite staat weer online en verdwijnt niet meer.',
      restoreFailed: 'Terughalen mislukt',
      movedToTrash: 'Naar de prullenbak',
      deleteFailed: 'Verwijderen mislukt',
      deleteHeading: '{name} verwijderen?',
      deleteBody: 'Deze hub bevat {sections} categorieën en {pages} pagina\u2019s. Ze blijven {days} dagen bewaard en zijn tot die tijd terug te halen; daarna zijn ze definitief weg.',
      deleteConfirmLabel: 'Typ {name} over om te bevestigen',
      deleteConfirm: 'Naar de prullenbak',
      ownerOnly: 'Alleen de account-eigenaar kan hubs beheren.',
      switcherLabel: 'Van hub wisselen',
      manage: 'Hubs beheren',
    },
    profile: {
      title: 'Profiel',
      subtitle: 'Beheer je accountgegevens',
      nameLabel: 'Naam',
      emailHint: 'Je e-mailadres is je inlognaam en kan nog niet gewijzigd worden.',
      saved: 'Profiel opgeslagen',
      savedBody: 'Je profielgegevens zijn bijgewerkt',
      saveError: 'Kon profielgegevens niet opslaan',
      passwordHeading: 'Wachtwoord wijzigen',
      passwordSubtitle:
        'Na het wijzigen blijven je huidige sessie actief en worden andere apparaten uitgelogd.',
      currentPassword: 'Huidig wachtwoord',
      newPassword: 'Nieuw wachtwoord',
      confirmPassword: 'Herhaal nieuw wachtwoord',
      passwordHint: 'Minimaal 8 tekens.',
      passwordMismatch: 'De nieuwe wachtwoorden komen niet overeen.',
      passwordTooShort: 'Het nieuwe wachtwoord moet minimaal 8 tekens lang zijn.',
      passwordChanged: 'Wachtwoord gewijzigd',
      passwordChangedBody: 'Je nieuwe wachtwoord is actief. Sessies op andere apparaten zijn uitgelogd.',
      passwordChangeError: 'Kon het wachtwoord niet wijzigen.',
      changePassword: 'Wachtwoord wijzigen',
    },
    communication: {
      title: 'Communicatie',
      subtitle: 'Welke e-mail dit account van ons ontvangt',
      marketingTitle: 'Nieuws en aanbiedingen',
      marketingDesc: 'Af en toe een mail over productnieuws en aanbiedingen. Servicemeldingen (onderhoud, facturen) sturen we altijd. Geldt voor het hele account.',
      marketingSavedBody: 'Je e-mailvoorkeur is opgeslagen',
      marketingErrorTitle: 'Opslaan niet gelukt',
      marketingErrorBody: 'Je e-mailvoorkeur kon niet worden opgeslagen. Probeer het opnieuw.',
      notManageable: 'Voor jouw rol valt hier niets in te stellen. Alleen de eigenaar van het account en het factuurcontact bepalen welke e-mail dit account ontvangt.',
    },
    accessibility: {
      title: 'Toegankelijkheid',
      subtitle: 'Pas de weergave aan jouw voorkeuren aan',
      savedBody: 'Je toegankelijkheidsvoorkeuren zijn bijgewerkt',
      reduceMotionTitle: 'Minder beweging',
      reduceMotionDesc: 'Verminder animaties en transities',
      highContrastTitle: 'Hoog contrast',
      highContrastDesc: 'Verhoog het contrast voor betere leesbaarheid',
      largeTextTitle: 'Grote tekst',
      largeTextDesc: 'Vergroot de tekstgrootte voor betere leesbaarheid',
      statementLink: 'Bekijk onze toegankelijkheidsverklaring →',
    },
    team: {
      title: 'Team',
      subtitle: 'Beheer wie toegang heeft tot deze omgeving',
      loading: 'Teamgegevens laden…',
      loadError: 'Kon de teamgegevens niet ophalen',
      seatsOf: 'van',
      seatUnitSingular: 'seat',
      seatUnitPlural: 'seats',
      seatsInUse: 'in gebruik',
      seatsHint: '(leden + openstaande uitnodigingen)',
      inviteHeading: 'Iemand uitnodigen',
      seatsFull:
        'Alle editorplekken zijn bezet. Verwijder eerst een lid of openstaande uitnodiging, of koop er een editor bij.',
      seatsFullLink: 'Naar facturering',
      hubAccessAll: 'Toegang tot alle hubs',
      hubAccessSome: 'Toegang tot {count} hub(s)',
      hubAccessChange: 'wijzigen',
      hubAccessHeading: 'Hubtoegang van {name}',
      hubAccessIntro: 'Bepaal in welke documentatiesites deze persoon mag werken. Wat hij niet mag zien, staat ook niet in zijn overzicht.',
      hubAccessAllOption: 'Alle hubs',
      hubAccessAllHint: 'Ook hubs die je later aanmaakt.',
      hubAccessSomeOption: 'Alleen deze hubs',
      hubAccessSomeHint: 'De rest is voor deze persoon onzichtbaar.',
      hubAccessSave: 'Toegang opslaan',
      hubAccessSaved: 'Hubtoegang bijgewerkt',
      hubAccessError: 'Wijzigen mislukt',
      roleLabel: 'Rol',
      member: 'Lid',
      owner: 'Eigenaar',
      invite: 'Uitnodigen',
      inviteSent: 'Uitnodiging verstuurd',
      inviteError: 'Uitnodigen mislukt',
      resent: 'Opnieuw verstuurd',
      resendError: 'Versturen mislukt',
      revoked: 'Ingetrokken',
      revokeError: 'Intrekken mislukt',
      roleChanged: 'Rol gewijzigd',
      roleChangeError: 'Wijzigen mislukt',
      removed: 'Verwijderd',
      removeError: 'Verwijderen mislukt',
      confirmRemove: 'Weet je zeker dat je {name} uit dit account wilt verwijderen?',
      membersHeading: 'Leden',
      you: '(jij)',
      makeOwner: 'Maak eigenaar',
      makeMember: 'Maak lid',
      removeLabel: '{name} verwijderen',
      pendingHeading: 'Openstaande uitnodigingen',
      invitedAs: 'Uitgenodigd als {role}',
      roleOwnerLower: 'eigenaar',
      roleMemberLower: 'lid',
      validUntil: '· geldig tot {date}',
      resendLabel: 'Uitnodiging opnieuw versturen',
      revokeLabel: 'Uitnodiging intrekken',
      ownerOnly: 'Alleen de account-eigenaar kan teamleden beheren.',
    },
    billing: {
      title: 'Abonnement & facturering',
      subtitle: 'Beheer het abonnement van deze omgeving',
      loading: 'Abonnementsgegevens laden…',
      loadError: 'Kon de abonnementsgegevens niet ophalen',
      paymentSuccess: 'Betaling gelukt',
      paymentSuccessBody: 'Je abonnement wordt geactiveerd — dit duurt hooguit een minuut.',
      checkoutError: 'Checkout niet gestart',
      checkoutErrorBody: 'Kon de betaalpagina niet openen. Probeer het later opnieuw.',
      checkoutPage: {
        loadingTitle: 'Betaalvenster openen…',
        loadingBody: 'Een moment, Paddle wordt geladen. Daarna ga je terug naar je omgeving.',
        doneTitle: 'Bedankt',
        doneBody: 'Paddle heeft je betaalgegevens ontvangen. Je kunt dit tabblad sluiten.',
        closedTitle: 'Betaalvenster gesloten',
        closedBody: 'Er is niets afgeschreven. Je kunt dit tabblad sluiten.',
        errorTitle: 'Het betaalvenster kon niet openen',
        missingReference: 'Deze betaallink is onvolledig. Start de betaling opnieuw vanaf de factuurpagina in je omgeving.',
        loadFailed: 'Er ging iets mis bij het laden van het betaalvenster. Probeer het later opnieuw.',
        backToEnvironment: 'Terug naar je omgeving',
      },
      canceled: 'Opgezegd',
      cancelError: 'Opzeggen mislukt',
      resumed: 'Teruggedraaid',
      resumeError: 'Terugdraaien mislukt',
      switched: 'Overgestapt',
      switchError: 'Overstappen niet gelukt',
      trialHeading: 'Proefperiode',
      trialLeft: 'Nog {days} (tot {date}). Activeer een abonnement om daarna door te kunnen werken.',
      daySingular: 'dag',
      dayPlural: 'dagen',
      trialEnded: 'De proefperiode is (bijna) afgelopen. Activeer een abonnement om door te kunnen werken.',
      starterActive: 'Starter-plan — actief',
      planActive: '{plan}-plan — actief',
      planEnterprise: 'Enterprise',
      gracePeriod:
        'Opgezegd: het abonnement loopt nog tot {date}. Daarna wordt de omgeving alleen-lezen.',
      pastDue:
        'De laatste betaling is niet gelukt. Paddle probeert het opnieuw; werk zo nodig je betaalgegevens bij via de link in de e-mail van Paddle.',
      activeRenews: 'Het abonnement loopt en wordt automatisch verlengd.',
      readOnly: 'Alleen-lezen',
      suspended: 'Gedeactiveerd',
      readOnlyBody: 'De documentatie is nog te bekijken en te exporteren, maar bewerken kan niet meer.',
      suspendedBody: 'De omgeving is op slot; de gegevens zijn nog niet verwijderd.',
      activateToRestore: 'Activeer een abonnement om alles direct te herstellen.',
      purgeWarning: 'Zonder abonnement wordt deze omgeving op {date} definitief verwijderd.',
      freePlan: 'Gratis plan',
      freePlanBody: '1 gebruiker en maximaal 5 Figma-bestanden. Meer nodig? Upgrade hieronder naar Starter.',
      grantedStarterBody:
        'Dit account heeft het Starter-plan toegekend gekregen; er loopt geen betaald abonnement en er wordt niets in rekening gebracht.',
      active: 'Actief',
      activeBody: 'Deze omgeving is actief.',
      starterLegend: 'Starter-plan',
      yearly: 'Jaarlijks',
      twoMonthsFree: '2 maanden gratis',
      yearlyPrice: '/jaar',
      yearlyPriceHint: '(€32,50/mnd)',
      monthly: 'Maandelijks',
      monthlyPrice: '/maand',
      activateStarter: 'Starter activeren',
      pricesNote: 'Prijzen exclusief btw. Je rekent veilig af via Paddle; het totaal zie je vóór het bevestigen.',
      useFreePlan: 'Of gebruik het gratis plan (1 gebruiker, 5 Figma-bestanden)',
      freeConfirm:
        'Overstappen naar het gratis plan? Dit kan alleen als je gebruik binnen de limieten past (1 gebruiker, 5 Figma-bestanden); anders vertellen we wat er eerst weg moet.',
      yesFreePlan: 'Ja, gratis plan',
      noCancel: 'Toch niet',
      resumeSubscription: 'Opzegging terugdraaien',
      cancelSubscription: 'Abonnement opzeggen',
      cancelConfirm:
        'Opzeggen per einde betaalperiode? Daarna is de omgeving nog 30 dagen alleen-lezen (voor export) en wordt de data uiteindelijk verwijderd.',
      yesCancel: 'Ja, opzeggen',
      ownerOnly: 'Alleen de account-eigenaar kan het abonnement beheren.',
      periodLegend: 'Factureringsperiode',
      planLegend: 'Kies een plan',
      twoMonthsFreeShort: '\u2212 2 mnd',
      planStarter: 'Starter',
      planTeam: 'Team',
      teamBadge: 'Tot 10 hubs',
      planStarterSummary: '3 editors, 2 hubs, 50 Figma-bestanden',
      planTeamSummary: '10 editors, 10 hubs, analytics en rechten per hub',
      activatePlan: '{plan} activeren',
      toYearlyHeading: 'Overstappen naar jaarlijks betalen',
      toYearlyBody: 'Je betaalt dan tien maanden in plaats van twaalf. Het verschil wordt meteen verrekend en de wijziging gaat direct in.',
      toMonthlyHeading: 'Overstappen naar maandelijks betalen',
      toMonthlyBody: 'Je houdt het jaar waarvoor je al betaald hebt. Pas als dat afloopt ga je per maand betalen — er wordt nu niets in rekening gebracht.',
      switchToYearly: 'Naar jaarlijks',
      switchToMonthly: 'Naar maandelijks',
      intervalChanged: 'Betaalperiode gewijzigd',
      intervalChangeError: 'Wisselen niet gelukt',
      switchHeading: 'Overstappen naar {plan}',
      switchToTeamBody: 'Team geeft je 10 editors, 10 hubs, analytics en rechten per hub. Bijgekochte editors die binnen het plan passen vervallen automatisch.',
      switchToStarterBody: 'Starter bevat 3 editors en 2 hubs. Heb je meer editors in gebruik, dan worden die als extra editors bijgekocht.',
      switchTo: 'Overstappen naar {plan}',
      planChanged: 'Plan gewijzigd',
      planChangeError: 'Overstappen niet gelukt',
      seatsHeading: 'Editors',
      seatsOfInUse: 'van {limit} in gebruik',
      seatsOccupancy: '{inUse} van {limit} editorplekken bezet',
      seatsIncludedRow: 'Inbegrepen bij {plan}',
      seatsPurchasedRow: 'Bijgekocht',
      seatsTotalRow: 'Totaal beschikbaar',
      seatsMembersRow: 'Leden',
      seatsInvitedRow: 'Uitgenodigd, nog niet geaccepteerd',
      seatsFreeRow: 'Nog vrij',
      seatsViewersFree: 'Kijkers zijn altijd gratis en tellen niet mee.',
      seatsExtraLabel: 'Extra editors',
      seatsIncrease: 'Eén editor erbij',
      seatsDecrease: 'Eén editor eraf',
      seatsPerMonth: 'per maand',
      seatsPerYear: 'per jaar',
      seatsUnitPrice: '{price} {period} per extra editor',
      seatsPreview: 'Je gaat naar {limit} editors, dus {amount} {period} extra.',
      seatsProration: 'Het verschil wordt verrekend op je eerstvolgende factuur.',
      seatsFloor: 'Lager kan niet: er zijn {inUse} plekken bezet. Verwijder eerst een lid of uitnodiging.',
      seatsSave: 'Wijziging doorvoeren',
      seatsUpdated: 'Editors bijgewerkt',
      seatsError: 'Wijzigen niet gelukt',
      usageHeading: 'Gebruik',
      usageStorage: 'Opslag',
      usageFiles: 'Figma-bestanden',
      usageOf: 'van {limit}',
      usageBar: '{used} van {limit} in gebruik',
      usageStorageNote:
        'Een richtlijn voor normaal gebruik (fair use), geen slot. Ga je eroverheen, dan blijft alles gewoon werken; we nemen contact op als het structureel zo blijft.',
      usageStorageOver:
        'Je zit boven de richtlijn van {limit}. Alles blijft gewoon werken; we nemen contact op als het hierbij blijft.',
      usageFilesNote: 'Bij {limit} bestanden weigert een nieuwe import.',
      usageFilesFull:
        'Je zit aan het maximum. Een nieuwe import weigert tot je een bestand verwijdert of overstapt naar een groter plan.',
      usageMeasured: 'Opslag gemeten op {date} en elke nacht bijgewerkt. De bestandsteller is actueel.',
    },
    about: {
      title: 'Over',
      subtitle: 'Versie-informatie van deze omgeving',
      versionLabel: 'Versie',
    },
  },

  access: {
    modes: {
      public: 'Openbaar',
      password: 'Wachtwoord',
      team: 'Alleen team',
    },

    adminBar: {
      title: 'Wie deze documentatie mag lezen: {mode}. Klik om te wijzigen.',
    },

    gate: {
      title: 'Deze documentatie is afgeschermd',
      categoryTitle: '{name} is afgeschermd',
      teamTitle: 'Deze documentatie is voor teamleden',
      body: 'Vul het wachtwoord in dat je hebt gekregen om verder te gaan.',
      categoryBody: 'Deze categorie heeft een eigen wachtwoord. Vul het in om verder te gaan.',
      teamBody: 'Log in met je account om deze documentatie te lezen.',
      passwordLabel: 'Wachtwoord',
      remember: 'Onthoud mij op dit apparaat',
      submit: 'Ontgrendelen',
      unlockSite: 'Ontgrendelen',
      wrongPassword: 'Dat wachtwoord klopt niet.',
      tooManyAttempts: 'Te veel pogingen. Wacht een minuut en probeer het opnieuw.',
      noLongerLocked: 'Deze documentatie is niet meer afgeschermd — laad de pagina opnieuw.',
      failed: 'Ontgrendelen mislukt. Probeer het opnieuw.',
      memberFooter: 'Hoor je bij het team?',
    },

    settings: {
      title: 'Toegang',
      subtitle: 'Bepaal wie je documentatie mag lezen.',
      heading: 'Wie mag deze documentatie lezen',
      headingHint: 'Geldt voor de hele site. Losse categorieën kunnen hiervan afwijken.',
      publicDesc: 'Iedereen met de link kan meelezen.',
      passwordDesc: 'Eén gedeeld wachtwoord dat je zelf uitdeelt.',
      teamDesc: 'Alleen mensen die zijn ingelogd en bij je team horen.',
      statusPublic: 'Je site is openbaar — iedereen met de link kan meelezen.',
      statusPassword: 'Je site is afgeschermd met een wachtwoord',
      statusTeam: 'Je site is alleen beschikbaar voor je team',
      since: ' sinds {date}.',
      newPassword: 'Nieuw wachtwoord',
      keepPassword: 'Leeg laten om het huidige wachtwoord te houden',
      passwordSet: 'Er staat een wachtwoord ingesteld. Vul een nieuw wachtwoord in om het te vervangen.',
      passwordRule: 'Minimaal 8 tekens. Dit wachtwoord wordt gedeeld, dus maak het makkelijk door te geven.',
      showPassword: 'Wachtwoord tonen',
      hidePassword: 'Wachtwoord verbergen',
      generate: 'Wachtwoord voorstellen',
      hintLabel: 'Hint voor bezoekers (optioneel)',
      hintPlaceholder: 'Vraag het je contactpersoon',
      hintHelp: 'Staat op het slotscherm. Zet hier nooit het wachtwoord zelf in.',
      copyInvite: 'Uitnodiging kopiëren',
      copied: 'Gekopieerd',
      copyFailed: 'Kopiëren naar het klembord is niet gelukt.',
      inviteIntro: 'Hier lees je onze design system-documentatie:',
      invitePassword: 'Wachtwoord: {password}',
      save: 'Toegangsinstellingen opslaan',
      saved: 'Toegangsinstellingen opgeslagen',
      savedBody: 'De nieuwe instelling geldt direct voor bezoekers.',
      saveFailed: 'Opslaan mislukt',
      loadFailed: 'De toegangsinstellingen konden niet geladen worden',
      sharedCaveat: 'Een gedeeld wachtwoord houdt je documentatie uit zoekmachines en weg bij toevallige bezoekers, maar wie het heeft kan het doorgeven. Voor toegang per persoon kies je "Alleen team".',
      categoriesHeading: 'Categorieën met een eigen slot',
      categoriesHint: 'Per categorie in te stellen via het ⋯-menu naast de naam.',
      noCategoryLocks: 'Geen enkele categorie wijkt af van de site-instelling.',
      seePlans: 'Bekijk de plannen',
    },

    category: {
      title: 'Toegang — {name}',
      legend: 'Wie mag deze categorie lezen',
      inherit: 'Volgt de site',
      inheritNow: 'Op dit moment: {mode}',
      publicDesc: 'Leesbaar, ook wanneer de rest van de site is afgeschermd.',
      passwordDesc: 'Een eigen wachtwoord, bovenop de site-instelling.',
      teamDesc: 'Alleen voor mensen die zijn ingelogd en bij je team horen.',
      newPassword: 'Nieuw wachtwoord',
      keepPassword: 'Leeg laten om het huidige wachtwoord te houden',
      visibilityLegend: 'Tonen in het menu?',
      visibilityLocked: 'Tonen met een slotje',
      visibilityLockedDesc: 'Lezers zien dat deze categorie bestaat en kunnen om toegang vragen.',
      visibilityHidden: 'Volledig verbergen',
      visibilityHiddenDesc: 'Onzichtbaar voor iedereen zonder toegang.',
      saved: 'Toegang voor {name} opgeslagen',
      saveFailed: 'De toegangsinstellingen konden niet opgeslagen worden',
      planRequired: 'Toegang per categorie hoort bij het Starter-plan.',
      lockedLabel: 'Afgeschermd',
      onBadge: 'Aan',
    },
  },

  settings: {
    title: 'Instellingen',
    insights: {
      title: 'Analytics',
      subtitle: 'Wordt je design system gebruikt, en waar loopt het spaak',
      loading: 'Rapport laden…',
      teamHeading: 'Hoort bij het Team-plan',
      teamBody: 'Analytics laat zien waar lezers naar zoeken zonder iets te vinden, welke componenten uit Figma nog geen documentatie hebben, en welke pagina\u2019s gelezen worden maar al lang niet zijn bijgewerkt.',
      periodLabel: 'Periode',
      days7: 'Afgelopen week',
      days30: 'Afgelopen maand',
      days90: 'Afgelopen kwartaal',
      gapsHeading: 'Gezocht, niets gevonden',
      gapsIntro: 'Dit zochten lezers zonder resultaat — gaten in je documentatie, in hun eigen woorden.',
      gapsEmpty: 'Elke zoekopdracht leverde iets op.',
      timesSearched: '{count}\u00d7 gezocht',
      adoptionHeading: 'Van Figma naar documentatie',
      adoptionSummary: '{components} componenten in Figma, {documented} gedocumenteerd, {read} daarvan ook echt gelezen.',
      adoptionEmpty: 'Nog geen Figma-componenten in deze hub. Koppel een bestand om te zien wat er wel en niet gedocumenteerd is.',
      inFigma: 'In Figma',
      undocumentedLabel: 'Zonder documentatie',
      documentedUnreadLabel: 'Beschreven, niet gelezen',
      undocumentedList: 'Deze componenten hebben nog geen pagina:',
      staleHeading: 'Gelezen, maar lang niet bijgewerkt',
      staleIntro: 'Deze pagina\u2019s worden gebruikt terwijl er al een tijd niets aan veranderd is.',
      viewsSince: '{count} weergaven',
      updated: 'bijgewerkt {date}',
      mostReadHeading: 'Meest gelezen',
      privacyNote: 'Er worden alleen aantallen per pagina per dag bewaard. Geen cookies, geen profielen, niets dat naar een individuele bezoeker herleidbaar is.',
    },
    nav: {
      figma: 'Figma bibliotheek',
      tokens: 'Design tokens',
      theme: 'Thema',
      access: 'Toegang',
      mcp: 'MCP-server',
      insights: 'Analytics',
      storybook: 'Storybook',
    },
    storybook: {
      title: 'Storybook',
      subtitle: 'Laat je draaiende componenten in de documentatie zien',
      intro:
        'Storybook is de aparte site waarop je gebouwde componenten los draaien. Stel hier één keer het adres in, dan kunnen redacteuren elke story op een pagina insluiten zonder telkens een volledig adres te plakken.',
      urlLabel: 'Adres van je Storybook',
      urlPlaceholder: 'https://storybook.voorbeeld.nl',
      urlHint:
        'Het adres waarop je gepubliceerde Storybook staat. Het moet met https:// beginnen en bereikbaar zijn vanuit de browser van de mensen die je styleguide lezen.',
      save: 'Opslaan',
      saving: 'Bezig met opslaan…',
      saved: 'Storybook-adres opgeslagen',
      disconnect: 'Loskoppelen',
      disconnected: 'Storybook losgekoppeld',
      connectedLabel: 'Nu gekoppeld aan',
      notConnected: 'Er is nog geen Storybook gekoppeld.',
      openStorybook: 'Storybook openen',
      privacyTitle: 'Wat dit wel en niet doet',
      privacyBody:
        'Een story wordt geladen door de browser van degene die de pagina leest, rechtstreeks vanaf jullie Storybook. Pentrical haalt hem nooit zelf op. Een Storybook achter het bedrijfsnetwerk blijft dus werken voor collega\'s op dat netwerk, en blijft voor de rest simpelweg leeg.',
      failed: 'Het adres kon niet worden opgeslagen.',
      check: {
        allowed: 'Deze Storybook laat zich insluiten',
        blocked: 'Deze Storybook weigert insluiten',
        unknown: 'We konden deze Storybook niet controleren',
        notChecked: 'Nog niet gecontroleerd',
        notCheckedBody:
          'We hebben nog niet gekeken of deze Storybook zich laat insluiten. Sommige servers verbieden dat, en dan blijft een story-blok bij elke lezer leeg.',
        allowedBody: 'Er staat niets in de weg. Een story op een pagina verschijnt gewoon bij je lezers.',
        xFrameOptionsBody:
          'De server achter dit adres zegt tegen elke browser dat zijn pagina\'s alleen door hemzelf ingesloten mogen worden. Een story-blok blijft daardoor leeg met een browserfout, en geen enkele instelling aan onze kant gaat daaroverheen — het is de andere server die dit bepaalt.',
        frameAncestorsBody:
          'De server achter dit adres houdt een lijst bij van sites die hem mogen insluiten, en deze styleguide staat daar niet op. Een story-blok blijft daardoor leeg met een browserfout.',
        unreachableBody:
          'Onze server kon dit adres niet bereiken. Dat hoeft geen probleem te zijn: een Storybook achter jullie bedrijfsnetwerk is voor ons onzichtbaar en werkt tegelijk prima voor collega\'s op dat netwerk, want de story wordt door hún browser geladen en niet door ons. Zet een story op een pagina om het zelf te zien.',
        redirectBody:
          'Dit adres stuurt door naar iets anders, dus zijn we gestopt. Vul het adres in waar de Storybook zelf staat, zonder die doorverwijzing.',
        headerLabel: 'De server stuurt mee:',
        fixIntro: 'Wie die server beheert kan het toestaan met deze regel, in plaats van de huidige:',
        fixNote:
          'De oude X-Frame-Options-regel moet weg, niet ernaast blijven staan — staan ze allebei, dan gedragen browsers zich onderling verschillend. In het adres hierboven staat de omgeving waarop je nu kijkt; werk je vanaf je eigen machine, vul dan het adres in dat je lezers straks gebruiken. Meerdere omgevingen mogen achter elkaar, gescheiden door een spatie.',
        recheck: 'Opnieuw controleren',
        checkedAt: 'Gecontroleerd op {when}',
      },
    },
    mcp: {
      scope: 'Wat mag een AI-tool lezen?',
      scopeDesc: 'Zet een categorie uit om hem buiten AI-tools te houden. Handig voor werk dat nog niet naar buiten mag, of afspraken die je wel met je team deelt maar niet met een taalmodel.',
      scopeHint: 'Dit verandert niets aan wat mensen in de browser zien — alleen wat er naar een AI-tool gaat.',
      figmaScope: 'Designs die een AI-tool mag lezen',
      figmaScopeDesc:
        'Een AI-tool kan ook de componenten zelf opvragen: afmetingen, kleuren, afstanden en gegenereerde code. Zet een bestand uit als die niet naar een externe AI-aanbieder mag.',
      figmaToggle: 'Designs uit {name} beschikbaar voor AI-tools',
      figmaExcludedBody: '{name} is niet meer leesbaar voor AI-tools',
      figmaIncludedBody: '{name} is weer leesbaar voor AI-tools',
      categoryInherited: 'Volgt de bovenliggende categorie',
      categoryHidden: 'Niet zichtbaar voor AI',
      categoryToggle: 'AI-toegang tot {name}',
      categorySaved: 'Opgeslagen',
      categoryExcludedBody: '"{name}" is nu niet meer zichtbaar voor AI-tools.',
      categoryIncludedBody: '"{name}" is weer zichtbaar voor AI-tools.',
      categoryFailed: 'De wijziging kon niet worden opgeslagen. Probeer het later opnieuw.',
      title: 'Pentrical MCP Server',
      subtitle: 'Laat Claude, Cursor of Copilot rechtstreeks uit dit design system lezen',
      explainer:
        'Zonder koppeling moet je documentatie kopiëren en plakken in een AI-tool. Met deze koppeling '
        + 'zoekt de AI-tool zelf de juiste pagina op zodra hij die nodig heeft — en gebruikt hij jullie '
        + 'eigen kleuren, afstanden en richtlijnen in plaats van standaardwaarden.',
      explainerScope:
        'De koppeling kan alleen lezen, en alleen wat gepubliceerd is. Concepten en verborgen '
        + "pagina's blijven buiten beeld.",
      step1: 'Stap 1 — maak een sleutel',
      step1Desc:
        'Geef de sleutel een naam waaraan je later herkent waar hij gebruikt wordt, bijvoorbeeld het '
        + 'apparaat of de tool.',
      nameLabel: 'Naam van de sleutel',
      namePlaceholder: 'Bijv. Cursor op mijn laptop',
      create: 'Sleutel aanmaken',
      created: 'Sleutel aangemaakt',
      createdBody: 'Kopieer de sleutel nu — hij is later niet meer op te vragen.',
      createFailed: 'Aanmaken mislukt',
      createFailedBody: 'De sleutel kon niet worden aangemaakt. Probeer het later opnieuw.',
      copyNow: 'Kopieer deze sleutel nu',
      copyNowDesc:
        'Dit is de enige keer dat je hem ziet. Ben je hem kwijt, dan trek je hem in en maak je een nieuwe.',
      tokenPlaceholder: 'JE-SLEUTEL',
      step2: 'Stap 2 — zet hem in je AI-tool',
      step2Desc: 'Kies hieronder de vorm die bij jouw tool past en plak die over.',
      addressLabel: 'Adres',
      claudeCode: 'Claude Code',
      otherTools: 'Cursor en VS Code',
      otherToolsHint:
        'Plak dit in het MCP-configuratiebestand van je tool. Staat de sleutel er niet in, dan is die '
        + 'inmiddels verborgen — maak een nieuwe aan.',
      simple: 'Koppelen',
      simpleDesc:
        'Plak dit adres in je AI-tool. Je logt daarna in met je eigen account en geeft toestemming — '
        + 'meer is er niet nodig.',
      simpleHint:
        'In Claude Desktop: Instellingen → Connectors → "Add custom connector". In Claude Code: '
        + 'claude mcp add --transport http pentrical, gevolgd door het adres hierboven.',
      connected: 'Gekoppelde AI-tools',
      showKeys: 'Liever een sleutel gebruiken?',
      hideKeys: 'Sleutels verbergen',
      keysWhen:
        'Alleen nodig voor tools die niet met inloggen overweg kunnen. Voor de meeste tools is het '
        + 'adres hierboven genoeg.',
      noEnvironment: 'Open eerst je documentatieomgeving',
      noEnvironmentDesc:
        'Je bent nu op het algemene adres van Pentrical, en daar staat geen documentatie. Ga naar de '
        + 'omgeving die je wilt koppelen — daar staat het juiste adres klaar.',
      desktop: 'Claude Desktop',
      desktopWhy:
        'Let op: alleen het adres invullen bij "Add custom connector" werkt niet. Claude Desktop '
        + 'zoekt dan naar een inlogdienst, en deze koppeling werkt met een sleutel. Kies daarom een '
        + 'van deze twee.',
      desktopOption1: '1. Via het koppelvenster (als je "Request headers" ziet staan)',
      desktopOption1Desc:
        'Vul het adres in, open "Request headers", kies "authorization" en plak hier deze waarde — '
        + 'inclusief het woord Bearer.',
      desktopOption2: '2. Via het configuratiebestand (werkt altijd)',
      desktopOption2Desc:
        'Zie je geen "Request headers"? Plak dit dan in claude_desktop_config.json en start Claude '
        + 'Desktop opnieuw op. Node.js moet geïnstalleerd zijn.',
      copy: 'Kopiëren',
      copied: 'Gekopieerd',
      copyFailed: 'Kopiëren mislukt',
      copyFailedBody: 'Je browser gaf geen toegang tot het klembord.',
      existing: 'Bestaande sleutels',
      noneYet: 'Je hebt nog geen sleutels aangemaakt.',
      lastUsed: 'Laatst gebruikt: {date}',
      never: 'nog niet gebruikt',
      revoke: 'Intrekken',
      revokeConfirm:
        'Sleutel "{name}" intrekken? Elke AI-tool die deze sleutel gebruikt, verliest direct toegang.',
      revoked: 'Sleutel ingetrokken',
      revokedBody: '"{name}" werkt niet meer.',
      revokeFailed: 'Intrekken mislukt',
      revokeFailedBody: 'De sleutel kon niet worden ingetrokken. Probeer het later opnieuw.',
      loadError: 'Kon de AI-koppeling niet laden.',
    },
    theme: {
      title: 'Thema',
      subtitle: 'Bepaal hoe de site eruitziet voor alle bezoekers.',
      invalidColor: 'Ongeldige kleur',
      invalidColorBody: 'Gebruik het formaat #rrggbb, bijvoorbeeld #1a73e8.',
      saved: 'Thema opgeslagen',
      savedBody: 'De look & feel is direct actief voor alle bezoekers.',
      saveFailed: 'Opslaan mislukt',
      save: 'Thema opslaan',
      resetAll: 'Alles terugzetten naar standaard',
      unsaved: 'Niet-opgeslagen wijzigingen',
      groupSans: 'Schreefloos (sans-serif)',
      groupSerif: 'Met schreef (serif)',
      groupMono: 'Vaste breedte (monospace)',
      logo: {
        heading: 'Logo',
        hint: 'Wordt linksboven in de header getoond. Maximale hoogte is 40 pixels.',
        typeLegend: 'Logotype',
        text: 'Tekst',
        textDesc: 'De sitenaam als tekstlogo.',
        textLabel: 'Logotekst',
        image: 'Afbeelding',
        imageDesc: 'Upload een JPG-, PNG- of WebP-bestand (max. 2 MB).',
        currentAlt: 'Huidig logo',
        uploading: 'Uploaden…',
        replace: 'Logo vervangen',
        upload: 'Logo uploaden',
        saved: 'Logo opgeslagen',
        settingSaved: 'Logo-instelling opgeslagen',
        textSaved: 'Logotekst opgeslagen',
        uploadFailed: 'Uploaden mislukt',
        remove: 'Logo verwijderen',
        removing: 'Verwijderen…',
        removeHint: 'Verwijder je het logo, dan komt de sitenaam weer als tekst in de header te staan.',
        confirmRemove: 'Logo verwijderen? De header toont dan weer de sitenaam als tekst.',
        removed: 'Logo verwijderd',
        removeFailed: 'Kon het logo niet verwijderen',
      },
      font: {
        heading: 'Lettertype',
        hint: 'Het basislettertype voor alle tekst op de site, geladen via het privacyvriendelijke Bunny.net.',
        baseLabel: 'Basislettertype',
        default: 'Standaard (Source Sans 3)',
        preview: 'Zo ziet lopende tekst eruit in dit lettertype — abcdefg ABCDEFG 0123456789.',
      },
      headings: {
        heading: 'Koppen',
        hint: 'Stel per kopniveau het lettertype, de grootte, de regelafstand en de kleur in. Leeg gelaten velden volgen de standaard.',
        fontLabel: 'Lettertype',
        baseOption: 'Basislettertype',
        sizeLabel: 'Grootte (px)',
        sizeOf: 'Grootte van {label}',
        lineHeightLabel: 'Regelafstand (\u00d7)',
        lineHeightHint: 'Keer de tekstgrootte: 1,4 bij een kop van 48px geeft ongeveer 67px.',
        lineHeightOf: 'Regelafstand van {label}',
        colorLabel: 'Kleur',
        defaultPlaceholder: 'standaard',
        breakpointLegend: 'Schermbreedte',
        desktop: 'Desktop',
        mobile: 'Mobiel',
        desktopHint: 'Geldt vanaf 640 pixels breed.',
        mobileHint: 'Geldt onder 640 pixels. Leeg laten gebruikt de desktopwaarde.',
        hasMobile: 'mobiel ingesteld',
        heroTitle: 'Hero-kop',
        heroTitleHint:
          'De kop in een hero-sectie. Laat je dit leeg, dan volgt hij H1 - vul je het in, dan kun je hem groter of strakker maken in die band over de volle breedte.',
        heroTitleFontNote:
          'Het lettertype volgt H1; de kleur stel je per hero-sectie in, afgestemd op de eigen achtergrond.',
        followsH1: 'volgt H1',
        h1: 'Kop 1 (paginatitel)',
        h2: 'Kop 2 (sectietitel)',
        h3: 'Kop 3',
        h4: 'Kop 4',
        colorOf: 'Kleur van {label}',
        hexOf: 'Hex-kleurcode van {label}',
        resetColorOf: 'Kleur van {label} terugzetten naar standaard',
      },
      colors: {
        heading: 'Kleuren',
        hint: 'Sitebrede kleuren. Leeg gelaten velden volgen de standaardvormgeving.',
        defaultPlaceholder: 'standaard',
        bgLabel: 'Achtergrondkleur van pagina’s',
        bgHint: 'De achtergrond achter alle pagina-inhoud.',
        borderLabel: 'Kleur van randen',
        borderHint: 'Randen van secties, kolommen en cards.',
        buttonLabel: 'Kleur van knoppen',
        buttonHint: 'Achtergrondkleur van primaire knoppen.',
        hexFor: 'Hex-kleurcode voor {label}',
        resetFor: '{label} terugzetten naar standaard',
      },
      appearance: {
        heading: 'Vormgeving',
        hint: 'Kies afgeronde of vierkante hoeken voor secties, kolommen, rijen, kaarten en afbeeldingen, en stel de afronding in. Een afbeelding in een rij met rand of achtergrond volgt de rij.',
        sections: 'secties',
        columns: 'kolommen',
        cards: 'kaarten',
        rows: 'rijen',
        images: 'afbeeldingen',
        roundedOf: 'Afgeronde hoeken van {label}',
        radiusLabel: 'Afronding',
      },
      footer: {
        heading: 'Footer',
        hint: 'Je eigen inhoud onderaan elke pagina, voor alle bezoekers.',
        showLabel: 'Footer tonen',
        hiddenWarning: 'De footer staat uit, dus eventuele inhoud die je erin hebt gezet (bijvoorbeeld een link naar je toegankelijkheidsverklaring) is nergens meer zichtbaar. De verklaring zelf blijft bereikbaar via /accessibility — overweeg om er vanuit je menu naar te linken.',
        contentLabel: 'Inhoud van de footer',
        contentHint: 'Leeg laten toont geen footer. Wil je een link naar je toegankelijkheidsverklaring (bijvoorbeeld naar /accessibility), voeg die hier zelf toe.',
        bgLabel: 'Achtergrondkleur van de footer',
        bgHint: 'De achtergrond achter de hele footer.',
        textLabel: 'Tekstkleur van de footer',
        textHint: 'Leeg laten leidt een leesbare kleur af uit de achtergrond.',
        autoPlaceholder: 'automatisch',
        textAutoReset: 'Terug naar automatische tekstkleur',
        readability: 'Leesbaarheid',
        sample: 'Zo komt de tekst in de footer eruit te zien.',
        contrastOk: 'Contrast {ratio}:1 — voldoet aan WCAG AA voor normale tekst.',
        contrastLow: 'Contrast {ratio}:1 — onder het WCAG AA-minimum van 4,5:1. Deze tekst is mogelijk slecht leesbaar.',
      },
      preview: {
        heading: 'Voorbeeld',
        hint: 'Zo komen de gekozen instellingen samen op een pagina.',
        h1: 'Kop 1 — paginatitel',
        h2: 'Kop 2 — sectietitel',
        h3: 'Kop 3 — tussenkop',
        h4: 'Kop 4 — subkop',
        body: 'Dit is een alinea lopende tekst in het basislettertype, zodat je de combinatie met de koppen kunt beoordelen.',
        button: 'Voorbeeldknop',
      },
    },
  },

  search: {
    open: 'Zoeken',
    shortcut: '⌘K',
    label: 'Zoeken in de documentatie',
    placeholder: 'Zoek in de documentatie…',
    hint: 'Typ om te zoeken in titels en inhoud',
    resultsLabel: 'Zoekresultaten',
    noResults: 'Geen resultaten voor "{query}"',
    toOpen: 'openen',
    toNavigate: '↑↓ om te kiezen',
  },

  componentStatus: {
    in_development: 'In ontwikkeling',
    beta: 'Beta',
    stable: 'Stabiel',
    deprecated: 'Vervangen',
  },

  mcpConnectBlock: {
    defaultTitle: 'Koppel je AI-tool aan dit design system',
    defaultIntro:
      'Laat je AI-assistent de richtlijnen, tokens en componentspecificaties rechtstreeks lezen, zodat hij de vastgestelde waarden gebruikt in plaats van ze te verzinnen.',
    copy: 'Kopiëren',
    copied: 'Gekopieerd',
    copyLabel: 'Koppeladres kopiëren',
    step1: '1. Open de instellingen van je AI-tool en voeg een MCP-server toe.',
    step2: '2. Plak het adres hierboven en log in met je account van deze omgeving.',
    step3: '3. Vraag je assistent daarna gerust naar een component of een kleur.',
    readOnly: 'De koppeling is alleen-lezen: een AI-tool kan niets wijzigen of publiceren.',
  },

  storybookBlock: {
    notConnected: 'Er is nog geen Storybook gekoppeld',
    notConnectedHint:
      'Een beheerder stelt het adres van jullie gepubliceerde Storybook één keer in, bij Instellingen → Storybook.',
    noStory: 'Er is nog geen story gekozen',
    noStoryHint:
      'Open de story in je Storybook en plak het adres in het veld hiernaast.',
    openInStorybook: 'Openen in Storybook',
    frameTitle: 'Storybook-story {story}',
  },

  tokens: {
    title: 'Design tokens',
    available: '{count} tokens beschikbaar',
    refresh: 'Vernieuwen',
    export: 'Exporteren',
    searchPlaceholder: 'Zoek tokens...',
    searchLabel: 'Zoek design tokens',
    loading: 'Tokens worden geladen...',
    emptyFound: 'Geen tokens gevonden',
    emptyNone: 'Nog geen design tokens',
    emptyFoundHint: 'Probeer een andere zoekterm',
    emptyNoneHint: 'Tokens komen uit de kleur- en tekststijlen van je Figma-bestanden',
    emptyToLibrary: 'Naar de Figma-bibliotheek',
    sources: 'Bronnen ({count}/{total})',
    sourcesTitle: 'Waar komen de tokens vandaan?',
    sourcesIntro:
      'Kies welke Figma-bestanden hun kleur- en tekststijlen als token leveren. Een prototype of een losse campagne laat je hier meestal uit: die stijlen horen niet in het design system.',
    sourcesStyles: '{colors} kleurstijlen, {texts} tekststijlen',
    sourcesToggleLabel: 'Tokens uit {name} gebruiken',
    sourcesCancel: 'Annuleren',
    sourcesSave: 'Opslaan',
    sourcesSaved: 'Bronnen bijgewerkt',
    sourcesSavedBody: 'De lijst bevat nu {count} tokens uit Figma',
    sourcesFailed: 'Opslaan mislukt',
    sourcesFailedBody: 'Kon de tokenbronnen niet bijwerken',
    filterSourceLabel: 'Filteren op bron',
    filterAll: 'Alle bronnen',
    filterFigma: 'Uit Figma',
    filterManual: 'Handmatig',
    filterCount: '{count} tokens',
    add: 'Token toevoegen',
    addTitle: 'Nieuw token',
    editTitle: 'Token wijzigen',
    figmaHint:
      'Dit token komt uit een Figma-stijl. De naam en de CSS-variabele kun je aanpassen — die horen bij je code. De waarde volgt de stijl en wordt bij elke synchronisatie opnieuw opgehaald.',
    fieldCategory: 'Categorie',
    fieldName: 'Naam',
    fieldNameHint: 'Punten maken groepen: spacing.md wordt spacing → md in de JSON-export.',
    fieldValue: 'Waarde',
    fieldCss: 'CSS-variabele',
    editLabel: '{name} wijzigen',
    deleteLabel: '{name} verwijderen',
    activateLabel: '{name} aanzetten',
    deactivateLabel: '{name} uitzetten',
    created: 'Token toegevoegd',
    updated: 'Token bijgewerkt',
    deleted: 'Token verwijderd',
    confirmDelete: 'Weet je zeker dat je "{name}" wilt verwijderen?',
    saveFailed: 'Opslaan mislukt',
    saveFailedBody: 'Kon het token niet opslaan',
    deleteFailed: 'Verwijderen mislukt',
    deleteFailedBody: 'Kon het token niet verwijderen',
    emptyCategory: 'Geen {category} tokens gevonden',
    colorPreview: 'Kleur preview: {value}',
    copyLabel: 'Kopieer {name}',
    inactive: 'Inactief',
    loadFailed: 'Laden mislukt',
    loadFailedBody: 'Kon design tokens niet laden',
    exportSuccess: 'Export succesvol',
    exportSuccessBody: 'Tokens geëxporteerd als {format}',
    exportFailed: 'Export mislukt',
    exportFailedBody: 'Kon tokens niet exporteren',
    copied: 'Gekopieerd',
    copiedBody: '{label} is gekopieerd naar klembord',
    copyFailed: 'Kopiëren mislukt',
    copyFailedBody: 'Kon de waarde niet kopiëren',
    categories: {
      color: 'Kleuren',
      typography: 'Typografie',
      spacing: 'Spacing',
      sizing: 'Sizing',
      shadow: 'Schaduwen',
      border: 'Borders',
    },
  },

  figma: {
    // Terugvalmeldingen wanneer de API zelf geen melding meestuurt.
    errors: {
      connectFailed: 'Kon geen verbinding maken met Figma',
      disconnectFailed: 'Kon de verbinding niet verbreken',
      filesFailed: 'Kon de bestanden niet ophalen',
      importFailed: 'Kon het bestand niet importeren',
      fileFailed: 'Kon het bestand niet ophalen',
      syncFailed: 'Synchroniseren mislukt',
      deleteFailed: 'Kon het bestand niet verwijderen',
      specsFailed: 'Kon de specificaties niet ophalen',
      codeFailed: 'Kon de code niet ophalen',
      codeGenerateFailed: 'Kon de code niet genereren',
    },
    common: {
      tryAgainLater: 'Probeer het later opnieuw',
      cancel: 'Annuleren',
      never: 'Nooit',
      copied: 'Gekopieerd',
      copiedBody: '{label} is gekopieerd naar klembord',
      copyFailed: 'Kopiëren mislukt',
      copyValueFailedBody: 'Kon de waarde niet kopiëren',
    },
    library: {
      title: 'Figma-bibliotheek',
      subtitle: 'Beheer en synchroniseer je Figma design bestanden',
      connected: 'Verbonden met Figma',
      notConnected: 'Niet verbonden',
      import: 'Importeer bestand',
      connect: 'Verbind met Figma',
      reconnect: 'Opnieuw koppelen',
      reconnectHint: 'Log opnieuw in bij Figma — gebruik dit als synchroniseren faalt met een autorisatiefout',
      disconnect: 'Ontkoppelen',
      disconnectHint: 'Stop met bijwerken vanuit Figma. Je bestanden en documentatie blijven staan',
      confirmDisconnect: 'Figma ontkoppelen van deze omgeving?\n\nJe geïmporteerde bestanden en documentatie blijven precies zoals ze zijn. Alleen het bijwerken vanuit Figma stopt, tot je opnieuw koppelt.',
      disconnected: 'Figma ontkoppeld',
      disconnectedBody: 'Je bestanden en documentatie zijn onaangeroerd. Koppel opnieuw wanneer je het bijwerken wilt hervatten.',
      searchPlaceholder: 'Zoek bestanden...',
      searchLabel: 'Zoek in Figma bestanden',
      loading: 'Bestanden worden geladen...',
      emptyFound: 'Geen bestanden gevonden',
      emptyNone: 'Nog geen bestanden',
      emptyFoundHint: 'Probeer een andere zoekterm',
      emptyNoneHint: 'Importeer je eerste Figma bestand om te beginnen',
      emptyNotConnectedHint: 'Verbind eerst met Figma; daarna importeer je hier je designbestanden',
      lostTitle: 'De koppeling met Figma moet vernieuwd worden',
      lostBody: 'Je documentatie blijft gewoon staan en zichtbaar. Alleen het automatisch bijwerken vanuit Figma is gestopt tot je opnieuw koppelt.',
      lostCause: 'Meestal komt dit doordat hetzelfde Figma-account elders opnieuw is gekoppeld, of doordat de toegang in Figma is ingetrokken.',
      listLabel: 'Figma bestanden',
      thumbnailAlt: 'Thumbnail van {name}',
      syncedAt: 'Gesynchroniseerd: {date}',
      syncLabel: 'Synchroniseer {name}',
      sync: 'Sync',
      openInFigma: 'Open {name} in Figma (opent in nieuw tabblad)',
      deleteLabel: 'Verwijder {name}',
      urlRequired: 'Figma URL is verplicht',
      urlInvalid: 'Voer een geldige Figma URL in',
      importStarted: 'Import gestart',
      importStartedBody: '{name} wordt op de achtergrond gesynchroniseerd',
      importFailed: 'Kon bestand niet importeren',
      alreadyImported: 'Dit Figma-bestand staat al in deze hub.',
      inOtherHub: 'Dit Figma-bestand staat al in de hub "{hub}". Een Figma-bestand kan in één hub tegelijk staan, dus hier kan het er niet ook bij. Haal het eerst weg uit "{hub}" als je het in deze hub wilt gebruiken.',
      inOtherHubUnnamed: 'Dit Figma-bestand is al in een andere hub van dit account in gebruik. Een Figma-bestand kan in één hub tegelijk staan, dus hier kan het er niet ook bij. Vraag de account-eigenaar om het daar eerst weg te halen.',
      withoutHub: 'Dit Figma-bestand staat nog in dit account geregistreerd, maar in geen enkele hub. Dat hoort niet te kunnen. Neem contact met ons op, dan zetten wij het recht.',
      syncStarted: 'Synchronisatie gestart',
      syncStartedBody: 'Het bestand wordt op de achtergrond gesynchroniseerd',
      syncFailed: 'Synchronisatie mislukt',
      confirmDelete: 'Weet je zeker dat je "{name}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
      deleted: 'Bestand verwijderd',
      deletedBody: '{name} is succesvol verwijderd',
      deleteFailed: 'Verwijderen mislukt',
      modalTitle: 'Figma bestand importeren',
      urlLabel: 'Figma URL',
      urlHint: 'Plak de URL van je Figma bestand hier',
      nameLabel: 'Naam (optioneel)',
      nameHint: 'Laat leeg om de naam uit Figma te gebruiken',
      namePlaceholder: 'Design System Components',
      importing: 'Importeren...',
      importAction: 'Importeren',
    },
    file: {
      loadError: 'Bestand niet gevonden',
      loadErrorBody: 'Kon het Figma bestand niet laden',
      back: 'Terug',
      nodes: 'nodes',
      viewer: 'Interactieve viewer',
      sync: 'Synchroniseren',
      openInFigma: 'Open in Figma',
      searchPlaceholder: 'Zoek nodes...',
      searchLabel: 'Zoek in nodes',
      gridView: 'Grid weergave',
      listView: 'Lijst weergave',
      loading: 'Laden...',
      emptyTitle: 'Geen nodes gevonden',
      emptyHintSearch: 'Probeer een andere zoekterm',
      emptyHintNone: 'Dit bestand bevat nog geen nodes',
      previewAlt: 'Preview van {name}',
      colName: 'Naam',
      colType: 'Type',
      colDimensions: 'Afmetingen',
      colActions: 'Acties',
      specsLabel: 'Design specificaties',
      types: {
        component: 'Componenten',
        frame: 'Frames',
        group: 'Groepen',
        text: 'Tekst',
        other: 'Overig',
      },
    },
    syncPopover: {
      title: 'Synchroniseren met Figma',
      fetching: 'Bestand ophalen bij Figma…',
      processing: 'Lagen verwerken: {done} van {total}',
      processingShort: 'Lagen verwerken…',
      finishing: 'Stijlen, tokens en previews bijwerken…',
      explainer: 'Pentrical haalt het complete bestand op en berekent per laag de specificaties. Bij grote bestanden kan dat een minuut duren - je kunt intussen gewoon verder werken.',
      dismiss: 'Voortgangsmelding verbergen',
      doneTitle: 'Synchronisatie klaar',
      doneBody: '{name} is bijgewerkt ({count} lagen).',
      failedTitle: 'Synchronisatie mislukt',
      failedBody: '{name} kon niet worden bijgewerkt. Probeer het later opnieuw.',
    },
    viewer: {
      chooseScreen: 'Blader door je bibliotheek',
      chooseScreenHint: 'Selecteer een scherm om de elementen en specificaties te bekijken.',
      screenSelectLabel: 'Scherm kiezen',
      page: 'Pagina',
      layers: 'Lagen',
      structureOf: 'Structuur van {name}',
      specs: 'Specs',
      viewLabel: 'Weergave',
      gridView: 'Rasterweergave met previews',
      grid: 'Raster',
      listView: 'Lijstweergave',
      list: 'Lijst',
      searchPlaceholder: 'Zoek een scherm...',
      searchLabel: 'Zoek een scherm',
      syncing: 'Synchronisatie bezig — de lijst wordt automatisch aangevuld.',
      previewsSkipped:
        'Bij dit grote bestand ({count} schermen) worden previews niet vooraf opgehaald. Open een scherm en de preview wordt direct geladen.',
      previewsLoading:
        'Previews worden opgehaald ({done} van {total}) — schermen zonder preview werken gewoon; de detailweergave laadt de preview zelf.',
      syncingTitle: 'Dit bestand wordt gesynchroniseerd',
      syncingBody:
        'Schermen verschijnen hier vanzelf zodra ze binnen zijn. Bij grote bestanden kan dit enkele minuten duren.',
      noScreens: 'Geen schermen gevonden. Synchroniseer het bestand eerst.',
      noSearchResults: 'Geen schermen gevonden voor "{query}".',
      screenSingular: 'scherm',
      screenPlural: 'schermen',
      loadingScreen: 'Scherm laden...',
      clickElement: 'Klik op een element in het design om de specificaties te bekijken.',
      frameLoadError: 'Scherm niet geladen',
      frameLoadErrorBody: 'Kon de inhoud van dit scherm niet ophalen',
      interiorsFailed: 'Details van componenten niet geladen',
      interiorsFailedBody: 'De lagen binnenin componenten zijn nu niet op te halen bij Figma. Klikken selecteert de component zelf.',
    },
    specs: {
      close: 'Paneel sluiten',
      tabSpecs: 'Specificaties',
      tabCode: 'Code',
      noSpecs: 'Geen specificaties beschikbaar',
      colorPreview: 'Kleur preview: {value}',
      copyLabel: 'Kopieer {label}',
      categories: {
        dimensions: 'Afmetingen',
        colors: 'Kleuren en borders',
        typography: 'Typografie',
        spacing: 'Spacing',
        effects: 'Effecten',
        layout: 'Layout',
      },
    },
    code: {
      copied: 'Code gekopieerd',
      copiedBody: '{format} code is gekopieerd naar klembord',
      copyFailed: 'Kopiëren mislukt',
      copyFailedBody: 'Kon de code niet kopiëren',
      generated: 'Code gegenereerd',
      generatedBody: 'De code is opnieuw gegenereerd',
      generateFailed: 'Genereren mislukt',
      generateFailedBody: 'Kon de code niet genereren',
      loading: 'Code wordt geladen...',
      noCode: 'Geen code beschikbaar voor dit formaat',
      generate: 'Genereer code',
      regenerateLabel: 'Regenereer {format} code',
      copyCodeLabel: 'Kopieer {format} code',
    },
    canvas: {
      label:
        'Canvas van {name}. Klik op een element voor specificaties, of gebruik de pijltjestoetsen om door de structuur te navigeren. Escape heft de selectie op.',
      zoom: 'Zoomen',
      zoomOut: 'Uitzoomen',
      zoomIn: 'Inzoomen',
      zoomFit: 'Passend zoomen',
      fit: 'Passend',
      actualSize: 'Werkelijke grootte (100%)',
      previewAlt: 'Voorbeeld van {name}',
      previewLoading: 'Voorbeeld wordt bij Figma opgehaald…',
      previewLoadingHint: 'Figma rendert het scherm live - grote schermen duren even.',
      previewUnavailable: 'Geen voorbeeld beschikbaar voor dit scherm',
    },
    picker: {
      libraryLoadError: 'Kon de Figma-bibliotheek niet laden.',
      framesLoadError: 'Kon de schermen van dit bestand niet laden.',
      backToFiles: 'Terug naar bestandenlijst',
      title: 'Figma-design kiezen',
      close: 'Sluiten',
      searchPlaceholder: 'Zoek een scherm op naam…',
      loadingLibrary: 'Bibliotheek laden…',
      noSyncedFiles: 'Geen gesynchroniseerde Figma-bestanden gevonden. Importeer eerst een bestand in de bibliotheek.',
      pageCount: "{count} pagina's",
      figmaFile: 'Figma-bestand',
      loadingScreens: 'Schermen laden…',
      noSearchResults: 'Geen schermen gevonden voor deze zoekopdracht.',
      noScreens: 'Dit bestand bevat geen schermen.',
    },
  },

  textStyles: {
    defaultSample: 'Wazig tv-filmpje bij de duik van een jonge cowboy',
    fontUnavailable:
      'Het voorbeeld gebruikt een vervangend lettertype: {fonts} is niet beschikbaar in de browser. De specificaties hieronder zijn wel de echte waarden uit Figma.',
    spec: {
      family: 'Lettertype',
      size: 'Grootte',
      lineHeight: 'Regelhoogte',
      letterSpacing: 'Letterafstand',
      transform: 'Kapitalisatie',
      decoration: 'Decoratie',
      paragraphSpacing: 'Alinea-afstand',
    },
    picker: {
      title: 'Tekststijlen kiezen',
      loading: 'Tekststijlen laden…',
      loadError: 'Kon de tekststijlen van dit bestand niet laden.',
      searchPlaceholder: 'Zoek een stijl op naam…',
      noStyles:
        'Geen tekststijlen gevonden in dit bestand. Synchroniseer het bestand opnieuw — stijlen worden tijdens een sync verzameld.',
      ungrouped: 'Overig',
      selectAll: 'Alles selecteren',
      deselectAll: 'Selectie opheffen',
      selectedCount: '{count} geselecteerd',
      confirm: 'Stijlen toevoegen',
      previewWord: 'Abc',
    },
  },

  colorStyles: {
    picker: {
      title: 'Kleurstijl kiezen',
      loading: 'Kleurstijlen laden…',
      loadError: 'Kon de kleurstijlen van dit bestand niet laden.',
      searchPlaceholder: 'Zoek een stijl op naam…',
      noStyles:
        'Geen kleurstijlen gevonden in dit bestand. Synchroniseer het bestand opnieuw — stijlen worden tijdens een sync verzameld.',
      ungrouped: 'Overig',
    },
  },

  pages: {
    status: {
      published: 'Gepubliceerd',
      draft: 'Concept',
      archived: 'Gearchiveerd',
    },
    // Terugvalmeldingen wanneer de API zelf geen melding meestuurt.
    errors: {
      fetchListFailed: 'Kon de pagina\'s niet ophalen',
      fetchFailed: 'Kon de pagina niet ophalen',
      homepageFailed: 'Kon de homepage niet laden',
      notFound: 'Pagina niet gevonden',
      createFailed: 'Kon de pagina niet aanmaken',
      updateFailed: 'Kon de pagina niet bijwerken',
      deleteFailed: 'Kon de pagina niet verwijderen',
      publishFailed: 'Kon de pagina niet publiceren',
      unpublishFailed: 'Kon de pagina niet depubliceren',
      duplicateFailed: 'Kon de pagina niet dupliceren',
    },
    noSection: 'Geen sectie',
    unknownSection: 'Onbekende sectie',
    tryAgainLater: 'Probeer het later opnieuw',
    cancel: 'Annuleren',
    list: {
      title: "Pagina's",
      stats: "{total} pagina's • {published} gepubliceerd • {draft} concepten",
      newPage: 'Nieuwe pagina',
      searchPlaceholder: "Zoek pagina's...",
      searchLabel: "Zoek in pagina's",
      filterStatus: 'Filter op status',
      allStatuses: 'Alle statussen',
      filterSection: 'Filter op sectie',
      allSections: 'Alle secties',
      loading: "Pagina's worden geladen...",
      emptyFiltered: "Geen pagina's gevonden",
      emptyNone: "Nog geen pagina's",
      emptyFilteredHint: 'Probeer andere filters',
      emptyNoneHint: 'Maak je eerste pagina aan om te beginnen',
      colTitle: 'Titel',
      colSection: 'Sectie',
      colStatus: 'Status',
      colUpdated: 'Laatst gewijzigd',
      colActions: 'Acties',
      actionsFor: 'Acties voor {title}',
      edit: 'Bewerken',
      view: 'Bekijken',
      publish: 'Publiceren',
      unpublish: 'Depubliceren',
      duplicate: 'Dupliceren',
      delete: 'Verwijderen',
      modalTitle: 'Nieuwe pagina aanmaken',
      titleLabel: 'Titel',
      titlePlaceholder: 'Bijv. Buttons',
      sectionLabel: 'Sectie',
      creating: 'Aanmaken...',
      create: 'Aanmaken',
      titleRequired: 'Titel is verplicht',
      created: 'Pagina aangemaakt',
      createdBody: '{title} is succesvol aangemaakt',
      createFailed: 'Aanmaken mislukt',
      published: 'Pagina gepubliceerd',
      publishedBody: '{title} is nu gepubliceerd',
      publishFailed: 'Publiceren mislukt',
      unpublished: 'Pagina gedepubliceerd',
      unpublishedBody: '{title} is nu een concept',
      unpublishFailed: 'Depubliceren mislukt',
      duplicated: 'Pagina gedupliceerd',
      duplicatedBody: 'Kopie van {title} is aangemaakt',
      duplicateFailed: 'Dupliceren mislukt',
      confirmDelete: 'Weet je zeker dat je "{title}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
      deleted: 'Pagina verwijderd',
      deletedBody: '{title} is succesvol verwijderd',
      deleteFailed: 'Verwijderen mislukt',
    },
    view: {
      learnMore: 'Meer informatie',
      loading: 'Pagina wordt geladen...',
      edit: 'Bewerken',
      noHomepageTitle: 'Nog geen homepage ingesteld',
      noHomepageHintPre: 'Open een pagina in de editor en zet daar de schakelaar',
      homepageToggleName: 'Als homepage instellen',
      noHomepageHintPost: 'aan om te bepalen wat bezoekers hier zien.',
      noHomepageVisitor: 'Er is nog geen startpagina beschikbaar.',
      notFoundTitle: 'Pagina niet gevonden',
      notFoundBody: 'De gevraagde pagina bestaat niet of is verwijderd.',
      backToHome: 'Terug naar home',
      breadcrumb: 'Breadcrumb',
      home: 'Home',
      lastUpdated: 'Laatst bijgewerkt: {date}',
      openImage: 'Afbeelding openen',
      viewSpecs: 'Bekijk specs van {name} in de interactieve viewer',
      figmaUnavailable: 'Het ingesloten Figma-design "{name}" is niet meer beschikbaar.',
      unknownBlock: 'Onbekend block type: {type}',
      empty: 'Deze pagina heeft nog geen inhoud.',
      imageUnavailable: 'Afbeelding niet beschikbaar',
      copyHexLabel: 'Kopieer hexwaarde {hex}',
      copyRgbLabel: 'Kopieer rgb-waarde {rgb}',
      copied: 'Gekopieerd',
      copiedBody: '{label} is gekopieerd naar klembord',
      copyFailed: 'Kopiëren mislukt',
      copyFailedBody: 'Kon de waarde niet kopiëren',
    },
  },
}
